import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Chip from '@mui/material/Chip';
import TableSortLabel from '@mui/material/TableSortLabel';
import { visuallyHidden } from '@mui/utils';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import ListItemText from '@mui/material/ListItemText';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  baseURL,
  decodeJWT,
  getUserWallets,
  smartAPY,
  getAIEngineInvestmentsByEmail,
} from '../../../services/api';
import Inex from '../../../assets/updated/buySell/INEX.svg';
import in500 from '../../../assets/token-icons/IN500_logo.png';
import inxc from '../../../assets/token-icons/INXC_logo.png';
import iusdp from '../../../assets/token-icons/IUSDP_logo.png';
import ripple from '../../../assets/updated/smartCrypto/ripple.png';
import surge from '../../../assets/updated/smartCrypto/surge.png';
import wave from '../../../assets/updated/smartCrypto/Wave.png';
import bullrun from '../../../assets/updated/smartCrypto/bullrun.png';
import cryptosImg from '../../../assets/updated/asset_wallet/crypto-04.png';
import blooming from '../../../assets/updated/smartCrypto/blomming.png';
import rush from '../../../assets/updated/smartCrypto/rush.png';
import xBitcoin from '../../../assets/updated/smartCrypto/x-bitcoin.png';
import aiInvestmentLogo from '../../../assets/updated/asset_wallet/ai-engine.png';
import bitcoinYayLogo from '../../../assets/updated/main-btcy-logo.svg';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import { Button, Paper, Typography } from '@mui/material';
import SellConfirmationPopup from './SellConfirmationPopup';
import { useTheme } from '@mui/material/styles';
import CongratulationsPopup from './Congratulations';
import SellCongratulations from './SellCongratulations';
import smartCryptoIcon from '../../../assets/updated/asset_wallet/smartCryptoAssetWallet.svg';
import xBlueIcon from '../../../assets/updated/asset_wallet/x-blueAssetWallet.svg';
import xBitcoinIcon from '../../../assets/updated/asset_wallet/x-bitcoinAssetWallet.svg';
import ContactAccountManagerPopup from './ContactPopup';
import cryptoTreasuryImg from '../../../assets/updated/asset_wallet/crypto-treasury.png';
import fiatImg from '../../../assets/updated/asset_wallet/fiat-logo.png';
import smartApyLogo from '../../../assets/updated/SmartApy/smartApyLogo.svg';
// Define the makeStyles hook
const useStyles = makeStyles((theme) => ({
  greenText: {
    color: `${theme.palette.success.main} !important`,
  },
  redText: {
    color: `${theme.palette.error.main} !important`,
  },
  flexContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  orderFirstOnTab: {
    [theme.breakpoints.down('md')]: {
      order: '-1',
    },
  },
}));

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: 'coin',
    numeric: false,
    disablePadding: true,
    label: 'Coin',
  },
  {
    id: 'amount',
    numeric: true,
    disablePadding: false,
    label: 'Crypto Amount/ USD',
  },
  {
    id: 'staking_balance',
    numeric: true,
    disablePadding: false,
    label: 'Staked Balance/ USD',
  },
  {
    id: 'coin_price',
    numeric: true,
    disablePadding: false,
    label: 'Coin Price in USD',
  },
  {
    id: 'todayPNL',
    numeric: true,
    disablePadding: false,
    label: 'Today‘s PnL',
  },
];

const COIN_HEADER_LEFT_OFFSET = 56;
const BITCOIN_YAY_LOGO_WRAPPER_SIZE = { xs: 96, sm: 112, md: 108 };
const BITCOIN_YAY_LOGO_HEIGHT = 70;

const SPECIAL_SMART_CRYPTO_EMAILS = new Set([
  'ssjriver@icloud.com',
  'pearlsblingsnthings@gmail.com',
]);

const normalizeEmail = (email) =>
  String(email || '')
    .trim()
    .toLowerCase();

const extractSmartCryptoYear = (note = '') => {
  const normalized = (note || '').toLowerCase();
  if (normalized.includes('2026')) return '2026';
  if (normalized.includes('2025')) return '2025';
  return null;
};

const getBullRunCategoryLabel = (note = '', email = '') => {
  const normalized = (note || '').toLowerCase();
  if (
    !(
      normalized.startsWith('xbitcoin bull-run') ||
      normalized.startsWith('xbbitcoin bull-run')
    )
  ) {
    return null;
  }

  const baseLabel =
    email === 'fowlertrucking14@yahoo.com'
      ? 'Smart Crypto x-Bitcoin Bull-Run($10,000 Investment + $10,000 Commission)'
      : 'Smart Crypto x-Bitcoin Bull-Run';

  if (!SPECIAL_SMART_CRYPTO_EMAILS.has(normalizeEmail(email))) {
    return baseLabel;
  }

  const year = extractSmartCryptoYear(note);
  return year ? `${baseLabel} ${year}` : baseLabel;
};

// robust date parser: accepts ISO string, number (ms), Date; else null
const safeDate = (v) => {
  if (!v) return null;
  if (v instanceof Date) return Number.isFinite(v.getTime()) ? v : null;
  const t = typeof v === 'number' ? v : Date.parse(v);
  return Number.isFinite(t) ? new Date(t) : null;
};

const formatDateTime = (d) =>
  d
    ? `${d.toLocaleDateString()} ${d.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })}`
    : '—';

const formatDateOnly = (d) => (d ? d.toLocaleDateString() : '—');

const fmtUSD = (n) =>
  Number.isFinite(Number(n))
    ? Number(n).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : '—';

const fmtQty = (n) =>
  Number.isFinite(Number(n))
    ? Number(n).toLocaleString(undefined, { maximumFractionDigits: 8 })
    : '—';

const fmtPrice = (n) =>
  Number.isFinite(Number(n))
    ? Number(n).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 6,
      })
    : '—';

const isBitcoinYayRow = (row) => {
  if (row?.coin !== 'BTCY') return false;
  const network = (row?.coinNetwork || '').toLowerCase();
  return network.includes('stellar') || network.includes('ying yang chain');
};

const isStellarBitcoinYayRow = (row) =>
  isBitcoinYayRow(row) &&
  (row?.coinNetwork || '').toLowerCase().includes('stellar');

const isYingYangChainRow = (row) =>
  isBitcoinYayRow(row) &&
  (row?.coinNetwork || '').toLowerCase().includes('ying yang chain');

const isCoinCategory = (category) =>
  category === 'Bitcoin Yay' || category?.includes('Coins');

const isBitcoinYaySelection = (value) =>
  value === 'Bitcoin Yay' || value === 'Bitcoin Yay Mint';

const formatCoinPriceCell = (coin, price) => {
  const n = Number(price);
  if (!Number.isFinite(n)) return '-';

  // Special-casing for a few tokens with bespoke formatting rules
  if (coin === 'WIBS') {
    if (n >= 1) return n.toFixed(2);
    if (n >= 0.01) return n.toFixed(4);
    if (n >= 0.0001) return n.toFixed(6);
    return n.toFixed(8);
  }
  if (coin === 'DaCrazy') {
    return n.toFixed(5);
  }

  // Default formatting for all other coins
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  });
};

const timeframeToDays = (tf) => {
  if (tf == null) return 0;
  const s = String(tf).trim().toLowerCase();

  let m = s.match(/^(\d+)\s*([hdmy])$/);
  if (m) {
    const v = Number(m[1]);
    const u = m[2];
    if (u === 'h') return v / 24;
    if (u === 'd') return v;
    if (u === 'm') return v * 30;
    if (u === 'y') return v * 365;
  }
  m = s.match(/^(\d+)\s*(hour|hours|hr|hrs|h)$/);
  if (m) return Number(m[1]) / 24;
  m = s.match(/^(\d+)\s*(day|days|d)$/);
  if (m) return Number(m[1]);
  m = s.match(/^(\d+)\s*(month|months|mo|mos|m)$/);
  if (m) return Number(m[1]) * 30;
  m = s.match(/^(\d+)\s*(year|years|yr|yrs|y)$/);
  if (m) return Number(m[1]) * 365;
  return 0;
};
// format basket to "SPX (60%), ADA/USD (40%)"
const formatBasket = (b) =>
  Array.isArray(b) && b.length
    ? b
        .map((x) =>
          [
            x?.symbol,
            Number.isFinite(Number(x?.weight)) ? `${x.weight}%` : undefined,
          ]
            .filter(Boolean)
            .join(' ')
        )
        .join(', ')
    : '-';

const aiHeadCells = [
  { id: 'createdAt', label: 'Date/Time' },
  { id: 'type', label: 'Type' },
  { id: 'asset', label: 'Asset' },
  { id: 'usdAmount', label: 'USD' },
  { id: 'amount', label: 'Qty' },
  { id: 'priceAtExecution', label: 'Exec Price' },
  { id: 'riskLevel', label: 'Risk' },
  { id: 'timeframe', label: 'Timeframe' },
  { id: 'status', label: 'Status' },
  { id: 'side', label: 'Side' },
  { id: 'basket', label: 'Basket' },
];

function EnhancedTableHead(props) {
  const { order, orderBy, onRequestSort, isMobile } = props;

  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  if (isMobile) {
    return null; // Don't render the table header on mobile devices
  }
  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? 'right' : 'left'}
            padding={headCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === headCell.id ? order : false}
            sx={{
              borderBottom: 'none !important',
              ...(headCell.id === 'coin'
                ? { paddingLeft: `${COIN_HEADER_LEFT_OFFSET}px` }
                : {}),
            }}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(['asc', 'desc']).isRequired,
  orderBy: PropTypes.string.isRequired,
  isMobile: PropTypes.bool.isRequired,
};

function SectionSubHeader({ isMobile }) {
  if (isMobile) return null;
  return (
    <TableRow>
      {headCells.map((h) => (
        <TableCell
          key={h.id}
          align={h.numeric ? 'right' : 'left'}
          padding={h.disablePadding ? 'none' : 'normal'}
          sx={{
            fontWeight: 700,
            color: 'rgba(255,255,255,0.85)',
            textTransform: 'none',
            ...(h.id === 'coin'
              ? { paddingLeft: `${COIN_HEADER_LEFT_OFFSET}px` }
              : {}),
          }}
        >
          {h.label}
        </TableCell>
      ))}
    </TableRow>
  );
}

export default function EnhancedTable({
  searchQuery,
  hideAssets,
  selectedValue,
  setupdatePlanMode,
  onPlanChange,
}) {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('calories');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dense, setDense] = useState(false);
  const [sellConfirmationPopup, setSellConfirmationPopup] = useState(false);
  const [contactPopup, setContactPopup] = useState(false);
  const [type, setType] = useState('');
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [userType, setUserType] = useState('Indexx Exchange');
  const [userSellPlanReformed, setUserPlanNameReformed] = useState('');
  const [userSellPlan, setUserPlanName] = useState('');
  const [CongratulationsPopup, setCongratulationsPopup] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState([]); // Track expanded categories
  const [isTotalAmount, setIsTotalAmount] = useState(false);
  const [smartApyData, setSmartApyData] = useState([]);
  const normalizedPath = (location.pathname || '').toLowerCase();
  const isBitcoinOnlyRoute =
    isBitcoinYaySelection(selectedValue) ||
    normalizedPath.includes('/wallet/nugget') ||
    normalizedPath.includes('bitcoin-yay');
  console.log('onPlanChange', onPlanChange);
  useEffect(() => {
    const user = localStorage.getItem('userType');
    if (user) {
      setUserType(user);
    }
  }, []);
  const [expandedLogos, setExpandedLogos] = useState({
    bitcoinYay: false,
    crypto: false,
    smartCrypto: false,
    fiat: false,
    aiInvestment: false,
  });
  const [planName, setPlanName] = useState('');
  const preferredOrder = ['INEX', 'INXC', 'IN500', 'DaCrazy', 'IUSD+', 'WIBS'];
  const [smartCryptoCoins, setSmartCryptoCoins] = useState([]);
  const [userEmail, setUserEmail] = useState('');
  const [aiInvestmentData, setAiInvestmentData] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const fetchAIEngine = async (email) => {
    try {
      setAiLoading(true);
      setAiError(null);
      const data = await getAIEngineInvestmentsByEmail(email);
      console.log('Data', data.data);
      setAiInvestmentData(
        data?.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      );
    } catch (e) {
      setAiError(e.message || 'Failed to load AI Engine investments');
    } finally {
      setAiLoading(false);
    }
  };
  const calculateTodayPNL = (item) => {
    const fixedTokens = ['INEX', 'IUSD+', 'INXC', 'IN500', 'WIBS', 'DaCrazy'];
    if (fixedTokens.includes(item.coinSymbol)) {
      return {
        value: '0.00',
        color: 'green',
      };
    }
    return {
      value: item.coinBalance > 0 ? (Math.random() * 10).toFixed(2) : '0.00',
      color: 'default',
    };
  };

  console.log('selectedValue', selectedValue);
  const fetchSmartAPY = async () => {
    const token = localStorage.getItem('access_token');

    // Check if token exists and is valid
    if (!token || token === 'null' || token === 'undefined') {
      console.log('No valid token found, skipping Smart APY fetch');
      return;
    }

    const decodedToken = decodeJWT(String(token));

    // Check if token was successfully decoded and has email
    if (!decodedToken || !decodedToken.email) {
      console.log('Invalid token or missing email, skipping Smart APY fetch');
      return;
    }

    try {
      const res = await smartAPY(decodedToken.email);
      setSmartApyData([...res.data].reverse());
    } catch (error) {
      console.error('Error fetching Smart APY data:', error);
    }
  };

  const getImage = (image) => {
    try {
      if (image === 'INEX') {
        return Inex;
      } else if (image === 'IN500') {
        return in500;
      } else if (image === 'INXC') {
        return inxc;
      } else if (image === 'IUSD+') {
        return iusdp;
      } else {
        return require(`../../../assets/token-icons/${image}.png`).default;
      }
    } catch (error) {
      return Inex;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        let email = String(localStorage.getItem('email'));
        setUserEmail(email);
        if (!email) {
          const signInToken = searchParams.get('signInToken');
          if (signInToken) {
            const decodedToken = await decodeJWT(signInToken);
            email = decodedToken.email;
          } else {
            window.location.href = `${baseURL}/auth/login?redirectWebsiteLink=exchange`;
            return;
          }
        }
        console.log('Email in fetch', email);
        // AI Engine fetch
        fetchAIEngine(email);

        const userWallets = await getUserWallets(email);
        const formattedData = userWallets.data.map((item) => {
          const coinBalance = Number(item.coinBalance);
          const coinPrice = Number(item.coinPrice);
          const coinPrevPrice = Number(item.coinPrevPrice);
          let todayPNL = null;
          let coin = item.coinSymbol;
          // Default percentage values for preferred coins
          const coinPercentageMap = {
            INEX: 8.33,
            INXC: 1.05,
            IN500: 2.83,
            'IUSD+': 1.0,
            WIBS: 1.58,
            Dacrazy: 1.25,
            daCrazy: 1.25,
            DaCrazy: 1.25,
          };

          // Check if the coin is one of the preferred ones
          if (coinPercentageMap[coin]) {
            // For preferred coins with no previous price (null or zero), directly use the default percentage
            todayPNL = {
              value: (
                (item?.coinStakedBalance > 0
                  ? item?.coinStakedBalance
                  : item?.coinBalance) * coinPrice
              ).toFixed(2),
              percentage: coinPercentageMap[coin].toFixed(2),
              isPositive: coinPercentageMap[coin] >= 0, // Default positive as it's predefined
            };
          } else if (
            coinBalance > 0 &&
            !isNaN(coinPrice) &&
            !isNaN(coinPrevPrice) &&
            coinPrevPrice !== 0
          ) {
            // Calculate PNL for other coins if valid coinPrevPrice exists
            const pnlValue = coinBalance * (coinPrice - coinPrevPrice);
            const pnlPercentage =
              ((coinPrice - coinPrevPrice) / coinPrevPrice) * 100;

            todayPNL = {
              value: pnlValue.toFixed(2),
              percentage: pnlPercentage.toFixed(2),
              isPositive: pnlValue >= 0,
            };
          } else {
            // If no valid PNL, set it to null
            todayPNL = null;
          }
          //Check if the notes start with the desired phrases
          const hasSmartCryptoNote =
            item.notes.startsWith('xBBitcoin Bull-Run') ||
            item.notes.startsWith('Smart Crypto Surge') ||
            item.notes.startsWith('Smart Crypto Ripple') ||
            item.notes.startsWith('Smart Crypto Wave') ||
            item.notes.startsWith('xBitcoin Blooming') ||
            item.notes.startsWith('xBitcoin Federal Reserve') ||
            item.notes.startsWith('xBitcoin Rush') ||
            item.notes.startsWith('xBitcoin Bitcoin') ||
            item.notes.startsWith('xBBitcoin Bitcoin2') ||
            item.notes.startsWith('xBBitcoin Bull-Run-2') ||
            item.notes.startsWith('xBBitcoin Bull-Run-3') ||
            item.notes.startsWith('xBBBitcoin Bull-Run-2025') ||
            item.notes.startsWith('xBBBitcoin Bull-Run Sep 2026') ||
            item.notes.startsWith('Bull-Run- 2/Jul 2025-Chris') ||
            item.notes.startsWith('xBitcoin Bull-Run');

          // if (hasSmartCryptoNote) {
          //   smartCryptoCoins.push(item); // Push to the smartCryptoCoins array
          // }

          // Filter and update smartCryptoCoins state
          const newSmartCryptoCoins = userWallets.data.filter(
            (item) =>
              item.notes.startsWith('xBBitcoin Bull-Run') ||
              item.notes.startsWith('Smart Crypto Surge') ||
              item.notes.startsWith('Smart Crypto Ripple') ||
              item.notes.startsWith('Smart Crypto Wave') ||
              item.notes.startsWith('xBitcoin Blooming') ||
              item.notes.startsWith('xBitcoin Federal Reserve') ||
              item.notes.startsWith('xBitcoin Rush') ||
              item.notes.startsWith('xBitcoin Bitcoin') ||
              item.notes.startsWith('xBBitcoin Bitcoin2') ||
              item.notes.startsWith('xBBitcoin Bull-Run-2') ||
              item.notes.startsWith('xBBitcoin Bull-Run-3') ||
              item.notes.startsWith('xBBBitcoin Bull-Run-2025') ||
              item.notes.startsWith('xBBBitcoin Bull-Run Sep 2026') ||
              item.notes.startsWith('Bull-Run- 2/Jul 2025-Chris') ||
              item.notes.startsWith('xBitcoin Bull-Run')
          );
          setSmartCryptoCoins(newSmartCryptoCoins);
          return {
            id: `${item.coinName}-${item.coinNetwork}`,
            coin: item.coinSymbol,
            amount: item.coinBalance,
            staking_balance: item.coinStakedBalance
              ? item.coinStakedBalance
              : 0,
            coin_price: item?.coinPrice,
            todayPNL: todayPNL,
            coinNetwork: item.coinNetwork,
            hasSmartCryptoNote: hasSmartCryptoNote, // Store flag for smart crypto
            notes: item.notes ? item.notes : '',
            amountInvested: item?.amountInvested ? item?.amountInvested : 0,
          };
        });

        // Ensure unique rows by coin name (id)

        const uniqueFormattedData = formattedData.filter(
          (value, index, self) =>
            index === self.findIndex((t) => t.id === value.id)
        );
        if (
          !email === 'dpar4fam@hotmail.com' ||
          !email === 'fowlertrucking14@yahoo.com'
        )
          setRows(uniqueFormattedData);
        else setRows(formattedData);
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    fetchSmartAPY();
  }, [navigate]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Normalize AI items so both "AI Engine Investment" tab and "Overview" can render safely
  const aiNormalized = useMemo(() => {
    const rows = Array.isArray(aiInvestmentData)
      ? aiInvestmentData
      : aiInvestmentData?.data || [];
    return (rows || []).map((r) => {
      const createdAt = safeDate(r.createdAt) || safeDate(r.startDate) || null;
      const tf = r.timeframe ?? null;
      const days = timeframeToDays(tf);
      const endDate =
        createdAt && days > 0
          ? new Date(createdAt.getTime() + days * 86400000)
          : null;

      return {
        id: r._id || r.id || `${Math.random()}`,
        createdAt, // Date|null
        type: r.type || r.strategyName || '-',
        asset: r.asset || '-',
        usdAmount: Number(r.usdAmount ?? r.invested), // may be NaN
        amount: Number(r.amount),
        priceAtExecution: Number(r.priceAtExecution ?? r.simulatedPrice),
        riskLevel: Number(r.riskLevel),
        timeframe: tf ? String(tf) : '—',
        status: (r.status || '—').toLowerCase(),
        side: (r.side || '—').toLowerCase(),
        basket:
          Array.isArray(r.basket) && r.basket.length
            ? r.basket
                .map((x) =>
                  [
                    x?.symbol,
                    Number.isFinite(Number(x?.weight))
                      ? `(${x.weight}%)`
                      : undefined,
                  ]
                    .filter(Boolean)
                    .join(' ')
                )
                .join(', ')
            : '—',
        endDate, // Date|null (not displayed by default)
      };
    });
  }, [aiInvestmentData]);

  console.log('aiNormalized', aiNormalized);
  const sortedRows = useMemo(() => {
    const smartCryptoRows = rows.filter((row) => row.hasSmartCryptoNote); // Filter rows with smart crypto notes
    const otherRows = rows.filter((row) => !row.hasSmartCryptoNote); // Filter other rows
    const sortedSmartCryptoRows = stableSort(
      smartCryptoRows,
      getComparator(order, orderBy)
    );
    const sortedOtherRows = stableSort(
      otherRows,
      getComparator(order, orderBy)
    );

    return [...sortedSmartCryptoRows, ...sortedOtherRows]; // Combine the sorted rows
  }, [rows, order, orderBy]);

  const filteredRows = useMemo(() => {
    // Add a priority rank to each row based on the preferred order array
    const rowsWithPriority = sortedRows.map((row) => {
      // Inject sold coin balances for donpanchos4me@gmail.com
      if (userEmail === 'donpanchos4me@gmail.com') {
        const soldBalances = {
          BTC: 0.1682002461,
          SOL: 8.747821792,
          ETH: 0.5441778415,
          LINK: 40.10608826,
          DOT: 95.36857271,
          BNB: 1.245786036,
          XRP: 454.2118918,
        };
        const overrideAmount = soldBalances[row.coin];
        if (overrideAmount) {
          row.amount = overrideAmount;
        }
      }
      return {
        ...row,
        priority: preferredOrder.includes(row.coin)
          ? preferredOrder.indexOf(row.coin)
          : preferredOrder.length,
      };
    });

    // Sort by priority first, then by the selected order and orderBy
    const sortedByPriority = stableSort(
      rowsWithPriority,
      (a, b) => a.priority - b.priority || getComparator(order, orderBy)(a, b)
    );

    const finalRows = sortedByPriority.filter((row) => {
      const matchesSearchQuery =
        row.coin.toLowerCase().includes(searchQuery.toLowerCase()) ||
        row.id.toLowerCase().includes(searchQuery.toLowerCase());
      console.log('matchesSearchQuery', row);
      if (!hideAssets && row.amount > 0) return matchesSearchQuery;

      const passesHideAssets =
        row.amount * row.coin_price >= 1 ||
        row.staking_balance > 0 ||
        (row.coin === 'USD' && row.amount > 0);
      return matchesSearchQuery && passesHideAssets;
    });

    console.log('smartCryptoCoins', selectedValue);
    // Additional filters based on `selectedValue`
    if (selectedValue === 'Fiat' || selectedValue === 'Fiat / Cash') {
      return finalRows.filter((row) => row.coin === 'USD');
    } else if (
      selectedValue === 'IUSD+' ||
      selectedValue === 'Smart APY' ||
      selectedValue === 'SmartAPY'
    ) {
      return finalRows.filter(
        (row) => row.coin === 'IUSD+' || row.coin === 'iUSD+'
      );
    } else if (selectedValue === 'Cryptos') {
      return finalRows.filter((row) => {
        const isSmartCryptoNote =
          row.notes.includes('xBBitcoin Bull-Run') ||
          row.notes.includes('Smart Crypto Surge') ||
          row.notes.includes('Smart Crypto Ripple') ||
          row.notes.includes('Smart Crypto Wave') ||
          row.notes.includes('xBitcoin Blooming') ||
          row.notes.includes('xBitcoin Federal Reserve') ||
          row.notes.includes('xBitcoin Rush') ||
          row.notes.includes('xBitcoin Bitcoin') ||
          row.notes.includes('xBBitcoin Bitcoin2') ||
          row.notes.includes('xBBitcoin Bull-Run-2') ||
          row.notes.includes('xBBBitcoin Bull-Run-2025') ||
          row.notes.includes('xBBBitcoin Bull-Run Sep 2026') ||
          row.notes.includes('Bull-Run- 2/Jul 2025-Chris') ||
          row.notes.includes('xBBitcoin Bull-Run-3') ||
          row.notes.includes('xBitcoin Bull-Run');
        return (
          !isSmartCryptoNote && row.coin !== 'USD' && !isBitcoinYayRow(row)
        );
      });
    } else if (isBitcoinYaySelection(selectedValue)) {
      return finalRows.filter((row) => isBitcoinYayRow(row));
    } else if (selectedValue === 'Smart Crypto') {
      return finalRows.filter((row) => {
        const isSmartCryptoNote =
          row.notes.includes('xBBitcoin Bull-Run') ||
          row.notes.includes('Smart Crypto Surge') ||
          row.notes.includes('Smart Crypto Ripple') ||
          row.notes.includes('Smart Crypto Wave') ||
          row.notes.includes('xBitcoin Blooming') ||
          row.notes.includes('xBitcoin Federal Reserve') ||
          row.notes.includes('xBitcoin Rush') ||
          row.notes.includes('xBitcoin Bitcoin') ||
          row.notes.includes('xBBitcoin Bitcoin2') ||
          row.notes.includes('xBBitcoin Bull-Run-2') ||
          row.notes.includes('xBBitcoin Bull-Run-3') ||
          row.notes.includes('xBBBitcoin Bull-Run-2025') ||
          row.notes.includes('xBBBitcoin Bull-Run Sep 2026') ||
          row.notes.includes('Bull-Run- 2/Jul 2025-Chris') ||
          row.notes.includes('xBitcoin Bull-Run');
        return isSmartCryptoNote; // Include only rows with Smart Crypto notes
      });
    } else if (selectedValue === 'AI Engine Investment') {
      // no coin-rows to show here; the AI Investment section renders from aiInvestmentData
      return aiNormalized;
    } else {
      // Default case for Overview or other values
      return finalRows;
      //.filter((row) => row.coin !== 'USD');
    }
  }, [
    sortedRows,
    searchQuery,
    hideAssets,
    order,
    orderBy,
    preferredOrder,
    selectedValue,
    smartCryptoCoins,
  ]);

  const visibleRows = useMemo(
    () => stableSort(filteredRows, getComparator(order, orderBy)),
    [order, orderBy, filteredRows]
  );

  if (loading) {
    return <div>Loading...</div>;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  // --- Simple pages (Fiat, Cryptos, Smart Crypto, IUSD+) ---
  // This must be placed after the AI Engine Investment branch and before the Overview renderer.
  if (['Fiat', 'IUSD+'].includes(selectedValue)) {
    return (
      <TableContainer>
        <Table
          sx={{
            minWidth: { xs: '100%', sm: 750 },
            '& .MuiTableCell-root': { border: 'none' }, // match the style you used for AI
          }}
        >
          <TableHead>
            <TableRow>
              {headCells.map((h) => (
                <TableCell
                  key={h.id}
                  sx={{ fontWeight: 700, color: 'text.primary' }}
                  align={
                    [
                      'amount',
                      'coin_price',
                      'staking_balance',
                      'value_usd',
                    ].includes(h.id)
                      ? 'right'
                      : 'left'
                  }
                >
                  {h.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {visibleRows.map((row) => (
              <TableRow
                key={row.id}
                hover
                sx={{ '&:last-child td': { border: 0 } }}
              >
                <TableCell>{row.coin}</TableCell>
                <TableCell align="right">
                  {Number(row.amount).toLocaleString(undefined, {
                    maximumFractionDigits: 8,
                  })}
                </TableCell>
                <TableCell align="right">
                  {Number(row.staking_balance ?? 0).toLocaleString()}
                </TableCell>
                <TableCell align="right">
                  {Number(row.coin_price ?? 0).toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 6,
                  })}
                </TableCell>
                {/* (Add any other columns you include in headCells) */}
              </TableRow>
            ))}

            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={headCells.length} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {selectedValue === 'Fiat'
                      ? 'No fiat assets available'
                      : 'No assets to display'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  if (selectedValue === 'AI Engine Investment') {
    const formatBasket = (basket = []) =>
      basket.length
        ? basket.map((b) => `${b.symbol} (${b.weight}%)`).join(', ')
        : '-';

    if (aiLoading) return <div>Loading AI Engine...</div>;
    if (aiError) return <div style={{ color: 'red' }}>Error: {aiError}</div>;

    // Apply search + hideAssets to AI data
    const q = (searchQuery || '').trim().toLowerCase();
    const filteredAI = (aiInvestmentData || [])
      .filter((r) => (hideAssets ? (Number(r.usdAmount) || 0) >= 1 : true))
      .filter((r) => {
        if (!q) return true;
        const hay = `${r.type ?? ''} ${r.asset ?? ''} ${r.status ?? ''} ${
          r.side ?? ''
        } ${r.timeframe ?? ''} ${formatBasket(r.basket)}`.toLowerCase();
        return hay.includes(q);
      });

    return (
      <TableContainer>
        <Table
          sx={{
            minWidth: { xs: '100%', sm: 750 },
            '& .MuiTableCell-root': {
              border: 'none', // This removes all table cell borders
            },
          }}
          aria-labelledby="tableTitle"
          size={dense ? 'small' : 'medium'}
        >
          <TableHead>
            <TableRow>
              {aiHeadCells.map((h) => (
                <TableCell
                  key={h.id}
                  sx={{ fontWeight: 700, color: 'text.primary' }}
                  align={
                    ['usdAmount', 'amount', 'priceAtExecution'].includes(h.id)
                      ? 'right'
                      : 'left'
                  }
                >
                  {h.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredAI.map((row) => (
              <TableRow
                key={row._id}
                hover
                sx={{ '&:last-child td': { border: 0 } }}
              >
                <TableCell>
                  {row.createdAt
                    ? new Date(row.createdAt).toLocaleString()
                    : '-'}
                </TableCell>

                <TableCell sx={{ textTransform: 'capitalize' }}>
                  {row.type ?? '-'}
                </TableCell>

                <TableCell>{row.asset ?? '-'}</TableCell>

                <TableCell align="right">
                  {row.usdAmount != null
                    ? Number(row.usdAmount).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : '-'}
                </TableCell>

                <TableCell align="right">
                  {row.amount != null
                    ? Number(row.amount).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 6,
                      })
                    : '-'}
                </TableCell>

                <TableCell align="right">
                  {row.priceAtExecution != null
                    ? Number(row.priceAtExecution).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 6,
                      })
                    : '-'}
                </TableCell>

                <TableCell>{row.riskLevel ?? '-'}</TableCell>
                <TableCell>{row.timeframe ?? '-'}</TableCell>

                <TableCell sx={{ textTransform: 'capitalize' }}>
                  {row.status ?? '-'}
                </TableCell>

                <TableCell sx={{ textTransform: 'capitalize' }}>
                  {row.side ?? '-'}
                </TableCell>

                <TableCell>
                  {Array.isArray(row.basket) && row.basket.length
                    ? row.basket
                        .map(
                          (b) => `${b.symbol} (${Number(b.weight).toFixed(0)}%)`
                        )
                        .join(', ')
                    : '-'}
                </TableCell>
              </TableRow>
            ))}

            {filteredAI.length === 0 && (
              <TableRow>
                <TableCell colSpan={aiHeadCells.length} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No AI Engine investments found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  // Find the first and last smart crypto coin rows based on the `smartCryptoCoins` list
  const firstSmartCryptoRowIndex = visibleRows.findIndex(
    (row) => row.hasSmartCryptoNote
  );
  console.log('firstSmartCryptoRowIndex', firstSmartCryptoRowIndex);

  const applySelectedValue = (organizedRows, selectedValue) => {
    if (isBitcoinYaySelection(selectedValue)) {
      const bitcoinYayCategory = organizedRows.find(
        (category) => category.category === 'Bitcoin Yay'
      );
      return bitcoinYayCategory ? [bitcoinYayCategory] : [];
    }

    if (['Fiat', 'IUSD+', 'Crypto'].includes(selectedValue)) {
      // Use only the "Other Coins" category
      const otherCoinsCategory = organizedRows.find(
        (category) => category.category === 'Other Coins'
      );

      if (!otherCoinsCategory) return []; // Return empty if no "Other Coins" category exists

      const filteredRows = otherCoinsCategory.rows.filter((row) => {
        if (selectedValue === 'Fiat') return row.coin === 'USD';
        if (selectedValue === 'IUSD+')
          return row.coin === 'IUSD+' || row.coin === 'iUSD+';
        return true; // Include all rows for "Crypto"
      });

      return filteredRows.length > 0
        ? [{ category: 'Other Coins', rows: filteredRows }]
        : [];
    } else if (['Smart Crypto'].includes(selectedValue)) {
      // Exclude the "Other Coins" category
      return organizedRows.filter(
        (category) =>
          category.category !== 'Other Coins' &&
          category.category !== 'Bitcoin Yay'
      );
    } else {
      return organizedRows;
    }
  };

  const groupedRows = (rows) => {
    const categories = {};

    // Helper function to determine all applicable categories for a row
    const getCategories = (row) => {
      const notes = row?.notes || '';
      let email = String(localStorage.getItem('email'));
      const applicableCategories = [];

      if (isBitcoinYayRow(row)) {
        applicableCategories.push('Bitcoin Yay');
        return applicableCategories;
      }

      if (notes.startsWith('xBitcoin Blooming'))
        applicableCategories.push('Smart Crypto x-Bitcoin Blooming');
      if (notes.startsWith('xBitcoin Federal Reserve'))
        applicableCategories.push('Smart Crypto x-Bitcoin Federal Reserve');
      if (notes.startsWith('xBitcoin Rush'))
        applicableCategories.push('Smart Crypto x-Bitcoin Rush');
      const bullRunCategory = getBullRunCategoryLabel(notes, email);
      if (bullRunCategory) {
        applicableCategories.push(bullRunCategory);
      }
      if (notes.startsWith('xBBitcoin Bull-Run-2'))
        applicableCategories.push('Smart Crypto x-Bitcoin Bull-Run-2');
      if (notes.startsWith('xBBitcoin Bull-Run-3'))
        applicableCategories.push('Smart Crypto x-Bitcoin Bull-Run-3');
      if (notes.startsWith('xBBBitcoin Bull-Run-2025'))
        applicableCategories.push('Smart Crypto x-Bitcoin Bull-Run-2025');
      if (notes.startsWith('xBBBitcoin Bull-Run Sep 2026'))
        applicableCategories.push('Smart Crypto x-Bitcoin Bull Run Sep 2026');
      if (notes.startsWith('xBBitcoin Bitcoin-Commission'))
        applicableCategories.push('Smart Crypto x-Bitcoin Bitcoin-Commission');
      if (notes.startsWith('xBitcoin Bitcoin'))
        applicableCategories.push('Smart Crypto x-Bitcoin Bitcoin');
      if (notes.startsWith('Bull-Run- 2/Jul 2025-Chris'))
        applicableCategories.push('Smart Crypto x-Bitcoin Bull-Run 2025 - New');
      if (notes.startsWith('xBBitcoin Bitcoin2'))
        applicableCategories.push('Smart Crypto x-Bitcoin Bitcoin-2');
      if (notes.includes('Wave'))
        applicableCategories.push('Smart Crypto x-Blue Wave');
      if (notes.includes('Surge'))
        applicableCategories.push('Smart Crypto x-Blue Surge');
      if (notes.includes('Ripple'))
        applicableCategories.push('Smart Crypto x-Blue Ripple');
      if (applicableCategories.length === 0)
        applicableCategories.push('Other Coins');
      return applicableCategories;
    };

    // Iterate over the rows to categorize them
    rows.forEach((row) => {
      const applicableCategories = getCategories(row); // Determine all applicable categories
      applicableCategories.forEach((category) => {
        if (!categories[category]) {
          categories[category] = []; // Initialize the category if not present
        }
        categories[category].push(row); // Add the row to all relevant categories
      });
    });

    // Remove duplicate rows within the "Other Coins" category
    if (categories['Other Coins']) {
      const uniqueOtherCoins = [];
      const seen = new Set();
      categories['Other Coins'].forEach((row) => {
        // row.id is `${item.coinName}-${item.coinNetwork}`
        if (!seen.has(row.id)) {
          uniqueOtherCoins.push(row);
          seen.add(row.id);
        }
      });
      categories['Other Coins'] = uniqueOtherCoins;
    }
    // Sort "Other Coins" based on preferredOrder, if present
    if (categories['Other Coins']) {
      categories['Other Coins'].sort((a, b) => {
        const aIndex = preferredOrder.indexOf(a.coin);
        const bIndex = preferredOrder.indexOf(b.coin);
        if (aIndex !== -1 && bIndex === -1) return -1;
        if (bIndex !== -1 && aIndex === -1) return 1;
        return aIndex - bIndex;
      });
    }

    // Sort the categories based on priority: xBitcoin first, xBlue second, Other Coins last
    const categoryPriority = [
      'Bitcoin Yay',
      'Other Coins',
      'Smart Crypto x-Bitcoin Blooming',
      'Smart Crypto x-Bitcoin Federal Reserve',
      'Smart Crypto x-Bitcoin Rush',
      'Smart Crypto x-Bitcoin Bull Run Sep 2026',
      'Smart Crypto x-Bitcoin Bull-Run 2026',
      'Smart Crypto x-Bitcoin Bull-Run 2025',
      'Smart Crypto x-Bitcoin Bull-Run',
      'Smart Crypto x-Bitcoin Bull-Run-2025',
      'Bull-Run- 2/Jul 2025-Chris',
      'Smart Crypto x-Bitcoin Bull-Run($10,000 Investment + $10,000 Commission)',
      'Smart Crypto x-Bitcoin Bull-Run-2',
      'Smart Crypto x-Bitcoin Bull-Run-3',
      'Smart Crypto x-BBitcoin Bitcoin-Commission',
      'Smart Crypto x-Bitcoin Bitcoin-Commission',
      'Smart Crypto x-Bitcoin Bitcoin',
      'Smart Crypto x-Bitcoin Bitcoin-2',
      'Smart Crypto x-Blue Wave',
      'Smart Crypto x-Blue Surge',
      'Smart Crypto x-Blue Ripple',
    ];

    const getCategoryIndex = (category) => {
      const idx = categoryPriority.indexOf(category);
      return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
    };

    const sortedCategories = Object.keys(categories).sort(
      (a, b) => getCategoryIndex(a) - getCategoryIndex(b)
    );

    // Transform sorted categories into an array of objects for structured output
    const organizedRows = sortedCategories.map((category) => ({
      category,
      rows: categories[category],
    }));

    console.log('organizedRows', organizedRows);
    const filteredOrganizedRows = applySelectedValue(
      organizedRows,
      selectedValue
    );
    console.log('filteredOrganizedRows', filteredOrganizedRows);

    return filteredOrganizedRows;
  };

  // Format categories
  const getFormattedCategory = (note) => {
    console.log('notes', note);
    // Define mappings for crypto types and managers
    const cryptoMappings = [
      'Bull-Run-2',
      'Bull-Run-3',
      'Bull-Run-2025',
      'Bull-Run-2026',
      'Bull-Run',
      'Bull-Run- 2/Jul 2025-Chris',
      'Bitcoin',
      'Blooming',
      'Federal Reserve',
      'Rush',
      'Ripple',
      'Wave',
      'Surge',
    ];
    const managerMappings = [
      'Omkar',
      'Kashir',
      'Issa',
      'Jan-2026',
      'Jan-2025',
      'Feb-2025',
      'Mar-2025',
      'Apr-2025',
      'May-2025',
      'Jun-2025',
      'Gabe',
      'Jan_2025($5,000)',
      'Jan_2025($6,000)',
    ];

    // Find the crypto type and manager from the note
    const cryptoType =
      cryptoMappings.find((type) => note.includes(type)) || 'Unknown Crypto';
    const managedBy =
      managerMappings.find((manager) => note.includes(manager)) || '';

    // Determine if it's a Smart Crypto type
    const isSmartCrypto = ['Ripple', 'Wave', 'Surge'].includes(cryptoType);
    const formattedCryptoType = isSmartCrypto ? `${cryptoType}` : cryptoType;
    console.log('formattedCryptoType', formattedCryptoType);

    // Format manager name - replace Jan-2025 with 2024 (inline approach) for specific email
    const formattedManager =
      managedBy === 'Jan-2025' && userEmail === 'chrishumpherys@yahoo.com'
        ? '2024'
        : managedBy;

    // Return formatted string
    return ` - ${formattedManager}`;
  };

  const getFormattedCategoryForImage = (note) => {
    console.log('notes', note);

    // Define mappings for crypto types
    const cryptoMappings = [
      'Bull-Run-2',
      'Bull-Run-3',
      'Bull-Run-2025',
      'Bull-Run-2026',
      'Bull-Run',
      'Bull-Run- 2/Jul 2025-Chris',
      'Bitcoin',
      'Blooming',
      'Federal Reserve',
      'Rush',
      'Ripple',
      'Wave',
      'Surge',
    ];

    // Match whole words using regex boundaries (\b) to prevent substring mismatches
    const cryptoType = cryptoMappings.find((type) =>
      new RegExp(`\\b${type}\\b`).test(note)
    );

    return cryptoType || 'Unknown Crypto';
  };

  const getPlanImage = (planName) => {
    console.log('planName', planName);
    if (planName.includes('Surge')) return surge;
    if (planName.includes('Wave')) return wave;
    if (planName.includes('Ripple')) return ripple;
    if (planName.includes('Blooming')) return blooming;
    if (planName.includes('Bull Run Sep 2026')) return bullrun;
    if (planName.includes('Bull-Run')) return bullrun;
    if (planName.includes('Bull-Run-2025')) return bullrun;
    if (planName.includes('Bull-Run- 2/Jul 2025-Chris')) return bullrun;
    if (planName.includes('Rush')) return rush;
    if (planName.includes('Bitcoin')) return xBitcoin;
    if (planName.includes('Federal Reserve')) return xBitcoin;
    //return xBitcoin; // Fallback in case no match is found
  };

  const organizedRows = groupedRows(visibleRows);
  const bitcoinYayGroups = organizedRows.filter(
    (group) => group.category === 'Bitcoin Yay'
  );
  const hasBitcoinYay = bitcoinYayGroups.some((group) => group.rows.length > 0);

  console.log(organizedRows);
  // Function to calculate the total for each group
  const calculateTotal = (rows) => {
    let totalAmount = 0;
    let totalStakingBalance = 0;

    rows.forEach((row) => {
      totalAmount += row.amount * row.coin_price;
      totalStakingBalance += row.staking_balance * row.coin_price;
    });

    return {
      totalAmount,
      totalStakingBalance,
    };
  };

  const getAdjustedPercentage = (coin, originalPercentage) => {
    console.log('coin', coin, originalPercentage);
    const percentageMap = {
      INEX: 8.33,
      INXC: 1.05,
      IN500: 2.83,
      'IUSD+': 1.0,
      WIBS: 0.58,
      Dacrazy: 0.25,
    };

    return percentageMap[coin] || originalPercentage; // Use the coin's specific percentage if available, otherwise fallback to the original
  };

  // Function to toggle expansion of a category
  const toggleCategory = (category) => {
    console.log('category', category);
    setExpandedCategories(
      (prev) =>
        prev.includes(category)
          ? prev.filter((cat) => cat !== category) // Collapse category
          : [...prev, category] // Expand category
    );
  };

  const toggleLogo = (logoType) => {
    setExpandedLogos((prev) => ({
      ...prev,
      [logoType]: !prev[logoType],
    }));
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <TableContainer>
        <Table
          sx={{
            minWidth: { xs: '100%', sm: 750 },
            '& .MuiTableCell-root': {
              border: 'none', // This removes all table cell borders
            },
          }}
          aria-labelledby="tableTitle"
          size={dense ? 'small' : 'medium'}
        >
          {selectedValue === 'Overview' ? (
            <TableBody>
              {/* Bitcoin Yay Section */}
              <TableRow>
                <TableCell
                  align="center"
                  colSpan={isMobile ? 3 : headCells.length + 2}
                  sx={{
                    paddingTop: '40px',
                    px: '0 !important',
                    textAlign: 'center !important',
                  }}
                >
                  <Box
                    onClick={() => toggleLogo('bitcoinYay')}
                    sx={{
                      cursor: 'pointer',
                      width: '100%',
                      display: 'flex !important',
                      justifyContent: 'center !important',
                      alignItems: 'center !important',
                    }}
                  >
                    <Box
                      component="img"
                      src={bitcoinYayLogo}
                      alt="Bitcoin Yay"
                      sx={{
                        width: 'auto',
                        transition: 'opacity 0.3s',
                        opacity: expandedLogos.bitcoinYay ? 1 : 0.85,
                        filter: 'brightness(1.2)',
                      }}
                      style={{
                        height: BITCOIN_YAY_LOGO_HEIGHT,
                        width: 'auto',
                      }}
                    />
                  </Box>
                </TableCell>
              </TableRow>
              {expandedLogos.bitcoinYay && (
                <>
                  <SectionSubHeader isMobile={isMobile} />
                  {hasBitcoinYay ? (
                    bitcoinYayGroups.map((group, groupIndex) => (
                      <React.Fragment key={`bitcoinyay-${groupIndex}`}>
                        {group.rows.map((row) => (
                          <TableRow
                            key={row.id}
                            sx={{ borderBottom: 'none !important' }}
                          >
                            <TableCell
                              component="th"
                              scope="row"
                              padding="none"
                              sx={{ borderBottom: 'none !important' }}
                            >
                              <ListItem
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  paddingLeft: 0,
                                }}
                              >
                                <ListItemAvatar
                                  sx={{ minWidth: COIN_HEADER_LEFT_OFFSET }}
                                >
                                  <Avatar>
                                    <Avatar
                                      alt={`${row.coin}`}
                                      src={getImage(row?.coin)}
                                    />
                                  </Avatar>
                                </ListItemAvatar>
                                <ListItemText
                                  primary={row.coin}
                                  secondary={`ID: ${row.id}`}
                                />
                                {isStellarBitcoinYayRow(row) && (
                                  <Chip
                                    size="small"
                                    label="BTCY Nugget"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ ml: 1 }}
                                  />
                                )}
                                {isYingYangChainRow(row) && (
                                  <Chip
                                    size="small"
                                    label="BTCY Token"
                                    color="warning"
                                    variant="outlined"
                                    sx={{ ml: 1 }}
                                  />
                                )}
                              </ListItem>
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ borderBottom: 'none !important' }}
                            >
                              {new Intl.NumberFormat('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 6,
                              }).format(row.amount)}{' '}
                              / $
                              {(row.amount * row.coin_price).toLocaleString(
                                undefined,
                                {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }
                              )}
                            </TableCell>
                            <TableCell
                              align="right"
                              sx={{ borderBottom: 'none !important' }}
                            >
                              {new Intl.NumberFormat('en-US', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 6,
                              }).format(row.staking_balance)}{' '}
                              / $
                              {(
                                row.staking_balance * row.coin_price
                              ).toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </TableCell>
                            {!isMobile && (
                              <>
                                <TableCell
                                  align="right"
                                  sx={{
                                    borderBottom: 'none !important',
                                    whiteSpace: 'nowrap',
                                    fontVariantNumeric: 'tabular-nums',
                                  }}
                                >
                                  {formatCoinPriceCell(
                                    row.coin,
                                    row.coin_price
                                  )}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ borderBottom: 'none !important' }}
                                  className={
                                    row.todayPNL
                                      ? row.todayPNL.isPositive
                                        ? classes.greenText
                                        : classes.redText
                                      : ''
                                  }
                                >
                                  {row.todayPNL
                                    ? `${
                                        row.todayPNL.value
                                      } (${getAdjustedPercentage(
                                        row.coin,
                                        row.todayPNL.percentage
                                      )}%)`
                                    : '0.00'}
                                </TableCell>
                              </>
                            )}
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell
                            colSpan={isMobile ? 3 : 5}
                            sx={{ textAlign: 'right' }}
                          >
                            Total Bitcoin Yay Amount in USD: $
                            {new Intl.NumberFormat('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(calculateTotal(group.rows).totalAmount)}
                            <br />
                            Total Bitcoin Yay Staked Balance in USD: $
                            {new Intl.NumberFormat('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(
                              calculateTotal(group.rows).totalStakingBalance
                            )}
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={isMobile ? 3 : 5}
                        sx={{ textAlign: 'center' }}
                      >
                        No Bitcoin Yay assets available
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
              {isBitcoinOnlyRoute ? null : (
                <>
                  {/* Horizontal divider after Bitcoin Yay section */}
                  <TableRow>
                    <TableCell
                      colSpan={isMobile ? 3 : 5}
                      sx={{
                        borderBottom: '1px solid #F3AF0E !important',
                        height: '1px',
                        padding: 0,
                      }}
                    />
                  </TableRow>
                  {/* Crypto Section */}
                  <TableRow>
                    <TableCell
                      colSpan={isMobile ? 3 : 5}
                      sx={{ textAlign: 'center', paddingTop: '40px' }}
                    >
                      <img
                        src={cryptosImg}
                        alt="Crypto Coins"
                        onClick={() => toggleLogo('crypto')}
                        style={{
                          cursor: 'pointer',
                          height: '50px',
                          transition: 'opacity 0.3s',
                          opacity: expandedLogos.crypto ? 1 : 0.7,
                        }}
                      />
                    </TableCell>
                  </TableRow>
                  {expandedLogos.crypto && (
                    <>
                      <SectionSubHeader isMobile={isMobile} />
                      {organizedRows.some(
                        (group) =>
                          group.category === 'Other Coins' &&
                          group.rows.some((row) => row.coin !== 'USD')
                      ) ? (
                        organizedRows
                          .filter((group) => group.category === 'Other Coins')
                          .map((group, groupIndex) => (
                            <React.Fragment key={groupIndex}>
                              {group.rows
                                .filter((row) => row.coin !== 'USD')
                                .map((row) => (
                                  <TableRow
                                    key={row.id}
                                    sx={{ borderBottom: 'none !important' }}
                                  >
                                    <TableCell
                                      component="th"
                                      scope="row"
                                      padding="none"
                                      sx={{ borderBottom: 'none !important' }}
                                    >
                                      <ListItem
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          paddingLeft: 0,
                                        }}
                                      >
                                        <ListItemAvatar
                                          sx={{
                                            minWidth: COIN_HEADER_LEFT_OFFSET,
                                          }}
                                        >
                                          <Avatar>
                                            <Avatar
                                              alt={`${row.coin}`}
                                              src={getImage(row?.coin)}
                                            />
                                          </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                          primary={row.coin}
                                          secondary={`ID: ${row.id}`}
                                        />
                                        {isStellarBitcoinYayRow(row) && (
                                          <Chip
                                            size="small"
                                            label="BTCY Nugget"
                                            color="warning"
                                            variant="outlined"
                                            sx={{ ml: 1 }}
                                          />
                                        )}
                                        {isYingYangChainRow(row) && (
                                          <Chip
                                            size="small"
                                            label="BTCY Token"
                                            color="warning"
                                            variant="outlined"
                                            sx={{ ml: 1 }}
                                          />
                                        )}
                                      </ListItem>
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{ borderBottom: 'none !important' }}
                                    >
                                      {new Intl.NumberFormat('en-US', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 6,
                                      }).format(row.amount)}{' '}
                                      / $
                                      {row.coin === 'USD'
                                        ? row.amount.toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })
                                        : (
                                            row.amount * row.coin_price
                                          ).toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{ borderBottom: 'none !important' }}
                                    >
                                      {new Intl.NumberFormat('en-US', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 6,
                                      }).format(row.staking_balance)}{' '}
                                      / $
                                      {(
                                        row.staking_balance * row.coin_price
                                      ).toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </TableCell>
                                    {!isMobile && (
                                      <>
                                        <TableCell
                                          align="right"
                                          sx={{
                                            borderBottom: 'none !important',
                                            whiteSpace: 'nowrap',
                                            fontVariantNumeric: 'tabular-nums',
                                          }}
                                        >
                                          {formatCoinPriceCell(
                                            row.coin,
                                            row.coin_price
                                          )}
                                        </TableCell>
                                        <TableCell
                                          align="right"
                                          sx={{
                                            borderBottom: 'none !important',
                                          }}
                                          className={
                                            row.todayPNL
                                              ? row.todayPNL.isPositive
                                                ? classes.greenText
                                                : classes.redText
                                              : ''
                                          }
                                        >
                                          {row.todayPNL
                                            ? `${
                                                row.todayPNL.value
                                              } (${getAdjustedPercentage(
                                                row.coin,
                                                row.todayPNL.percentage
                                              )}%)`
                                            : '0.00'}
                                        </TableCell>
                                      </>
                                    )}
                                  </TableRow>
                                ))}
                              <TableRow>
                                <TableCell
                                  colSpan={isMobile ? 3 : 5}
                                  sx={{ textAlign: 'right' }}
                                >
                                  Total Crypto Amount in USD: $
                                  {new Intl.NumberFormat('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }).format(
                                    calculateTotal(group.rows).totalAmount
                                  )}
                                  <br />
                                  Total Staked Balance in USD: $
                                  {new Intl.NumberFormat('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }).format(
                                    calculateTotal(group.rows)
                                      .totalStakingBalance
                                  )}
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          ))
                      ) : (
                        <TableRow>
                          <TableCell
                            colSpan={isMobile ? 3 : 5}
                            sx={{ textAlign: 'center' }}
                          >
                            No Crypto Assets available
                          </TableCell>
                        </TableRow>
                      )}
                    </>
                  )}
                </>
              )}
              {/* Horizontal divider after Crypto section */}
              <TableRow>
                <TableCell
                  colSpan={isMobile ? 3 : 5}
                  sx={{
                    borderBottom: '1px solid #079639 !important',
                    height: '1px',
                    padding: 0,
                  }}
                />
              </TableRow>
              {/* Smart Crypto Section */}
              <TableRow>
                <TableCell
                  colSpan={isMobile ? 3 : 5}
                  sx={{ textAlign: 'center', paddingTop: '40px' }}
                >
                  <Box
                    onClick={() => toggleLogo('smartCrypto')}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '20px',
                    }}
                  >
                    <img
                      src={cryptoTreasuryImg}
                      alt="Smart Crypto"
                      style={{
                        height: '50px',
                        transition: 'opacity 0.3s',
                        opacity: expandedLogos.smartCrypto ? 1 : 0.7,
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        color: '#F3AF0E !important',
                        fontSize: '20px',
                        fontStyle: 'italic',
                      }}
                    >
                      Crypto Treasury
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
              {expandedLogos.smartCrypto && (
                <>
                  {organizedRows.some(
                    (group) =>
                      group.category.includes('Smart Crypto') &&
                      group.rows.length > 0
                  ) ? (
                    <>
                      {/* Top separator for x-Bitcoin section */}
                      <TableRow>
                        <TableCell
                          colSpan={isMobile ? 3 : 5}
                          sx={{
                            borderBottom: '1px solid #FFA500 !important',
                            height: '1px',
                            padding: 0,
                          }}
                        />
                      </TableRow>
                      {/* Render xBitcoin Heading */}
                      {organizedRows.some((g) =>
                        g.category.includes('x-Bitcoin')
                      ) && (
                        <>
                          <TableRow>
                            <TableCell
                              colSpan={isMobile ? 3 : 5}
                              sx={{
                                borderBottom: 'none',
                                fontWeight: 'bold',
                                fontSize: '24px',
                                paddingTop: '60px',
                                paddingBottom: '0px',
                                color:
                                  // userType === 'Indexx Exchange'
                                  //   ? theme.palette.primary.main
                                  //   :
                                  '#FFA500',
                                textAlign: 'center',
                              }}
                            >
                              <img src={smartCryptoIcon} />
                              <img src={xBitcoinIcon} />
                            </TableCell>
                          </TableRow>

                          {organizedRows
                            .filter((group) =>
                              group.category.includes('x-Bitcoin')
                            )
                            .map((group, groupIndex) => (
                              <React.Fragment key={`xbitcoin-${groupIndex}`}>
                                <TableRow>
                                  <TableCell
                                    colSpan={isMobile ? 3 : 5}
                                    sx={{
                                      borderBottom: 'none',
                                      fontWeight: 'bold',
                                      color:
                                        // userType === 'Indexx Exchange'
                                        //   ? theme.palette.primary.main
                                        // :
                                        '#FFA500',
                                      textAlign: 'center',
                                    }}
                                  >
                                    {group.category}{' '}
                                    {getFormattedCategory(group.rows[0].notes)}
                                    {userEmail ===
                                      'donpanchos4me@gmail.com' && (
                                      <Typography
                                        style={{
                                          fontWeight: 800,
                                          color: 'red',
                                        }}
                                      >
                                        This package was sold by owner on 12th
                                        March
                                      </Typography>
                                    )}
                                  </TableCell>
                                </TableRow>
                                {userEmail !== 'donpanchos4me@gmail.com' && (
                                  <TableRow
                                    sx={{ borderBottom: 'none !important' }}
                                  >
                                    <TableCell
                                      colSpan={isMobile ? 1 : 2}
                                      sx={{ fontSize: '22px' }}
                                    >
                                      Estimated Value: $
                                      {(() => {
                                        let bonusAdded = false; // Track if the $300 bonus was applied

                                        const total = group.rows.reduce(
                                          (sum, row) => {
                                            let value =
                                              row.amount * row.coin_price;

                                            if (
                                              !bonusAdded &&
                                              (userEmail ===
                                                'paul@diversifiedlandscape.com' ||
                                                userEmail ===
                                                  'bmoralez12@gmail.com')
                                            ) {
                                              value += 3000; // Apply bonus only once
                                              bonusAdded = true;
                                            }

                                            return sum + value;
                                          },
                                          0
                                        );

                                        return new Intl.NumberFormat('en-US', {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        }).format(total);
                                      })()}
                                    </TableCell>
                                    <TableCell
                                      colSpan={isMobile ? 2 : 2}
                                      sx={{ fontSize: '22px' }}
                                    >
                                      Invested Amount: $
                                      {new Intl.NumberFormat('en-US', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }).format(
                                        group.rows.reduce(
                                          (total, row) =>
                                            total + row.amountInvested,
                                          0
                                        )
                                      )}
                                    </TableCell>
                                    {!isMobile && (
                                      <TableCell
                                        colSpan={1}
                                        sx={{ fontSize: '22px' }}
                                      >
                                        Total Value: $
                                        {(() => {
                                          let bonusAdded = false;

                                          const total = group.rows.reduce(
                                            (sum, row) => {
                                              let value =
                                                row.amount * row.coin_price +
                                                (row?.staking_balance || 0) *
                                                  row.coin_price;

                                              if (
                                                !bonusAdded &&
                                                (userEmail ===
                                                  'paul@diversifiedlandscape.com' ||
                                                  userEmail ===
                                                    'bmoralez12@gmail.com')
                                              ) {
                                                value += 3000;
                                                bonusAdded = true;
                                              }

                                              return sum + value;
                                            },
                                            0
                                          );

                                          return new Intl.NumberFormat(
                                            'en-US',
                                            {
                                              minimumFractionDigits: 2,
                                              maximumFractionDigits: 2,
                                            }
                                          ).format(total);
                                        })()}
                                      </TableCell>
                                    )}
                                  </TableRow>
                                )}
                                <TableRow>
                                  <TableCell
                                    colSpan={isMobile ? 3 : 5}
                                    sx={{ border: 'none !important' }}
                                  >
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        gap: '15px',
                                      }}
                                    >
                                      <Button
                                        disableRipple
                                        sx={{
                                          maxWidth: '250px',
                                          width: '100%',
                                          color:
                                            // userType === 'Indexx Exchange'
                                            //   ? theme.palette.primary.main
                                            //   :
                                            '#FFA500',
                                        }}
                                        onClick={() =>
                                          toggleCategory(group.category)
                                        }
                                      >
                                        {expandedCategories.includes(
                                          group.category
                                        )
                                          ? 'Hide Details'
                                          : 'See Details'}
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                                {expandedCategories.includes(
                                  group.category
                                ) && (
                                  <>
                                    <SectionSubHeader isMobile={isMobile} />
                                    {group.rows.map((row) => (
                                      <TableRow
                                        key={row.id}
                                        sx={{
                                          borderBottom: 'none !important',
                                        }}
                                      >
                                        <TableCell
                                          component="th"
                                          scope="row"
                                          padding="none"
                                          sx={{
                                            borderBottom: 'none !important',
                                          }}
                                        >
                                          <ListItem
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              paddingLeft: 0,
                                            }}
                                          >
                                            <ListItemAvatar
                                              sx={{
                                                minWidth:
                                                  COIN_HEADER_LEFT_OFFSET,
                                              }}
                                            >
                                              <Avatar>
                                                <Avatar
                                                  alt={`${row.coin}`}
                                                  src={getImage(row?.coin)}
                                                />
                                              </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                              primary={row.coin}
                                              secondary={`ID: ${row.id}`}
                                            />
                                            {isStellarBitcoinYayRow(row) && (
                                              <Chip
                                                size="small"
                                                label="BTCY Nugget"
                                                color="warning"
                                                variant="outlined"
                                                sx={{ ml: 1 }}
                                              />
                                            )}
                                            {isYingYangChainRow(row) && (
                                              <Chip
                                                size="small"
                                                label="BTCY Token"
                                                color="warning"
                                                variant="outlined"
                                                sx={{ ml: 1 }}
                                              />
                                            )}
                                          </ListItem>
                                        </TableCell>
                                        <TableCell align="right">
                                          {new Intl.NumberFormat('en-US', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 6,
                                          }).format(row.amount)}{' '}
                                          / $
                                          {(
                                            row.amount * row.coin_price
                                          ).toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}
                                        </TableCell>
                                        <TableCell align="right">
                                          {new Intl.NumberFormat('en-US', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 6,
                                          }).format(row.staking_balance)}{' '}
                                          / $
                                          {(
                                            row.staking_balance * row.coin_price
                                          ).toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}
                                        </TableCell>
                                        {!isMobile && (
                                          <>
                                            <TableCell align="right">
                                              {formatCoinPriceCell(
                                                row.coin,
                                                row.coin_price
                                              )}
                                            </TableCell>
                                            <TableCell
                                              align="right"
                                              className={
                                                row.todayPNL
                                                  ? row.todayPNL.isPositive
                                                    ? classes.greenText
                                                    : classes.redText
                                                  : ''
                                              }
                                            >
                                              {row.todayPNL
                                                ? `${
                                                    row.todayPNL.value
                                                  } (${getAdjustedPercentage(
                                                    row.coin,
                                                    row.todayPNL.percentage
                                                  )}%)`
                                                : '0.00'}
                                            </TableCell>
                                          </>
                                        )}
                                      </TableRow>
                                    ))}
                                  </>
                                )}
                                {expandedCategories.includes(
                                  group.category
                                ) && (
                                  <TableRow>
                                    <TableCell
                                      colSpan={isMobile ? 3 : 5}
                                      sx={{ border: 'none !important' }}
                                    >
                                      <div className={classes.flexContainer}>
                                        <div></div>
                                        <div
                                          style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            gap: '15px',
                                            alignItems: 'center',
                                            width: '100%',
                                          }}
                                        >
                                          {![
                                            'donpanchos4me@gmail.com',
                                            'donpanchos4mr@gmail.com',
                                            'dpar4fam@hotmail.com',
                                          ].includes(userEmail) && (
                                            <>
                                              <Button
                                                sx={{
                                                  maxWidth: '150px',
                                                  width: '100%',
                                                  color:
                                                    // userType ===
                                                    // 'Indexx Exchange'
                                                    //   ? theme.palette.primary
                                                    //       .main
                                                    //   :
                                                    '#FFA500',
                                                  borderColor: 'none',
                                                  '&:hover': {
                                                    color:
                                                      // userType ===
                                                      // 'Indexx Exchange'
                                                      //   ? theme.palette.primary
                                                      //       .main
                                                      //   :
                                                      '#FFA500',
                                                    borderColor: 'none',
                                                    opacity: '.7',
                                                    background: 'none',
                                                  },
                                                }}
                                                onClick={() => {
                                                  setContactPopup(true);
                                                  setType('Switch');
                                                }}
                                              >
                                                Switch Plan
                                              </Button>
                                              <Button
                                                sx={{
                                                  maxWidth: '150px',
                                                  width: '100%',
                                                  color:
                                                    // userType ===
                                                    // 'Indexx Exchange'
                                                    //   ? theme.palette.primary
                                                    //       .main
                                                    //   :
                                                    '#FFA500',
                                                  borderColor: 'none',
                                                  '&:hover': {
                                                    color:
                                                      // userType ===
                                                      // 'Indexx Exchange'
                                                      //   ? theme.palette.primary
                                                      //       .main
                                                      //   :
                                                      '#FFA500',
                                                    borderColor: 'none',
                                                    opacity: '.7',
                                                    background: 'none',
                                                  },
                                                }}
                                                onClick={() => {
                                                  setContactPopup(true);
                                                  setType('Sell');
                                                  setPlanName(
                                                    group.rows[0].notes
                                                  );
                                                  localStorage.setItem(
                                                    'SellPlanCurrencies',
                                                    JSON.stringify(group)
                                                  );
                                                }}
                                              >
                                                Sell Plan
                                              </Button>
                                            </>
                                          )}
                                          {[
                                            'donpanchos4me@gmail.com',
                                            'donpanchos4mr@gmail.com',
                                            'dpar4fam@hotmail.com',
                                          ].includes(userEmail) && (
                                            <Typography
                                              style={{
                                                fontWeight: 800,
                                                color: 'red',
                                              }}
                                            >
                                              This account is already closed or
                                              paid
                                            </Typography>
                                          )}
                                        </div>
                                        <div
                                          className={classes.orderFirstOnTab}
                                        >
                                          <Button
                                            variant="outlined"
                                            sx={{
                                              minWidth: '250px',
                                              width: '100%',
                                              color:
                                                // userType === 'Indexx Exchange'
                                                //   ? theme.palette.primary.main
                                                //   :
                                                '#FFA500',
                                              borderColor:
                                                // userType === 'Indexx Exchange'
                                                //   ? theme.palette.primary.main
                                                //   :
                                                '#FFA500',
                                            }}
                                            onClick={() =>
                                              navigate('/smart-crypto')
                                            }
                                          >
                                            Invest In New Package
                                          </Button>
                                        </div>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )}
                                {/* Bottom separator for this package */}
                                <TableRow>
                                  <TableCell
                                    colSpan={isMobile ? 3 : 5}
                                    sx={{
                                      borderBottom:
                                        '1px solid #FFA500 !important',
                                      height: '1px',
                                      padding: 0,
                                    }}
                                  />
                                </TableRow>
                              </React.Fragment>
                            ))}
                        </>
                      )}

                      {/* Render xBlue Heading */}
                      {organizedRows.some((g) =>
                        g.category.includes('x-Blue')
                      ) && (
                        <>
                          <TableRow>
                            <TableCell
                              colSpan={isMobile ? 3 : 5}
                              sx={{
                                borderBottom: 'none',
                                fontWeight: 'bold',
                                fontSize: '24px',
                                paddingTop: '60px',
                                paddingBottom: '0px',

                                color:
                                  // userType === 'Indexx Exchange'
                                  //   ? theme.palette.primary.main
                                  //   :
                                  '#FFA500',
                                textAlign: 'center',
                              }}
                            >
                              <img src={smartCryptoIcon} />
                              <img src={xBlueIcon} />
                            </TableCell>
                          </TableRow>

                          {/* Render all x-Blue packages */}
                          {organizedRows
                            .filter((group) =>
                              group.category.includes('x-Blue')
                            )
                            .map((group, groupIndex) => (
                              <React.Fragment key={`xblue-${groupIndex}`}>
                                <TableRow>
                                  <TableCell
                                    colSpan={isMobile ? 3 : 5}
                                    sx={{
                                      borderBottom: 'none',
                                      fontWeight: 'bold',
                                      color:
                                        // userType === 'Indexx Exchange'
                                        //   ? theme.palette.primary.main
                                        //   :
                                        '#FFA500',
                                      textAlign: 'center',
                                    }}
                                  >
                                    {group.category}{' '}
                                    {getFormattedCategory(group.rows[0].notes)}
                                    {userEmail ===
                                      'donpanchos4me@gmail.com' && (
                                      <Typography
                                        style={{
                                          fontWeight: 800,
                                          color: 'red',
                                        }}
                                      >
                                        This package was sold by owner on 12th
                                        March
                                      </Typography>
                                    )}
                                  </TableCell>
                                </TableRow>
                                {userEmail !== 'donpanchos4me@gmail.com' && (
                                  <TableRow
                                    sx={{ borderBottom: 'none !important' }}
                                  >
                                    <TableCell
                                      colSpan={isMobile ? 1 : 2}
                                      sx={{ fontSize: '22px' }}
                                    >
                                      Estimated Value: $
                                      {new Intl.NumberFormat('en-US', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }).format(
                                        group.rows.reduce(
                                          (total, row) =>
                                            total + row.amount * row.coin_price,
                                          0
                                        )
                                      )}
                                    </TableCell>
                                    <TableCell
                                      colSpan={isMobile ? 2 : 2}
                                      sx={{ fontSize: '22px' }}
                                    >
                                      Invested Amount: $
                                      {new Intl.NumberFormat('en-US', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }).format(
                                        group.rows.reduce(
                                          (total, row) =>
                                            total + row.amountInvested,
                                          0
                                        )
                                      )}
                                    </TableCell>
                                    {!isMobile && (
                                      <TableCell
                                        colSpan={1}
                                        sx={{ fontSize: '22px' }}
                                      >
                                        Total Value: $
                                        {new Intl.NumberFormat('en-US', {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        }).format(
                                          group.rows.reduce(
                                            (total, row) =>
                                              total +
                                              row.amount * row.coin_price +
                                              row?.staking_balance *
                                                row?.coin_price,
                                            0
                                          )
                                        )}
                                      </TableCell>
                                    )}
                                  </TableRow>
                                )}
                                <TableRow>
                                  <TableCell
                                    colSpan={isMobile ? 3 : 5}
                                    sx={{ border: 'none !important' }}
                                  >
                                    <div
                                      style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        gap: '15px',
                                      }}
                                    >
                                      <Button
                                        disableRipple
                                        sx={{
                                          maxWidth: '250px',
                                          width: '100%',
                                          color:
                                            // userType === 'Indexx Exchange'
                                            //   ? theme.palette.primary.main
                                            //   :
                                            '#FFA500',
                                        }}
                                        onClick={() =>
                                          toggleCategory(group.category)
                                        }
                                      >
                                        {expandedCategories.includes(
                                          group.category
                                        )
                                          ? 'Hide Details'
                                          : 'See Details'}
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                                {expandedCategories.includes(
                                  group.category
                                ) && (
                                  <>
                                    <SectionSubHeader isMobile={isMobile} />
                                    {group.rows.map((row) => (
                                      <TableRow
                                        key={row.id}
                                        sx={{ borderBottom: 'none !important' }}
                                      >
                                        <TableCell
                                          component="th"
                                          scope="row"
                                          padding="none"
                                        >
                                          <ListItem
                                            sx={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              paddingLeft: 0,
                                            }}
                                          >
                                            <ListItemAvatar
                                              sx={{
                                                minWidth:
                                                  COIN_HEADER_LEFT_OFFSET,
                                              }}
                                            >
                                              <Avatar>
                                                <Avatar
                                                  alt={`${row.coin}`}
                                                  src={getImage(row?.coin)}
                                                />
                                              </Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                              primary={row.coin}
                                              secondary={`ID: ${row.id}`}
                                            />
                                          </ListItem>
                                        </TableCell>
                                        <TableCell align="right">
                                          {new Intl.NumberFormat('en-US', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 6,
                                          }).format(row.amount)}{' '}
                                          / $
                                          {(
                                            row.amount * row.coin_price
                                          ).toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}
                                        </TableCell>
                                        <TableCell align="right">
                                          {new Intl.NumberFormat('en-US', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 6,
                                          }).format(row.staking_balance)}{' '}
                                          / $
                                          {(
                                            row.staking_balance * row.coin_price
                                          ).toLocaleString(undefined, {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          })}
                                        </TableCell>
                                        {!isMobile && (
                                          <>
                                            <TableCell align="right">
                                              {formatCoinPriceCell(
                                                row.coin,
                                                row.coin_price
                                              )}
                                            </TableCell>
                                            <TableCell
                                              align="right"
                                              className={
                                                row.todayPNL
                                                  ? row.todayPNL.isPositive
                                                    ? classes.greenText
                                                    : classes.redText
                                                  : ''
                                              }
                                            >
                                              {row.todayPNL
                                                ? `${
                                                    row.todayPNL.value
                                                  } (${getAdjustedPercentage(
                                                    row.coin,
                                                    row.todayPNL.percentage
                                                  )}%)`
                                                : '0.00'}
                                            </TableCell>
                                          </>
                                        )}
                                      </TableRow>
                                    ))}
                                  </>
                                )}
                                {expandedCategories.includes(
                                  group.category
                                ) && (
                                  <TableRow>
                                    <TableCell
                                      colSpan={isMobile ? 3 : 5}
                                      sx={{ border: 'none !important' }}
                                    >
                                      <div className={classes.flexContainer}>
                                        <div></div>
                                        <div
                                          style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            gap: '15px',
                                            alignItems: 'center',
                                            width: '100%',
                                          }}
                                        >
                                          {![
                                            'donpanchos4me@gmail.com',
                                            'donpanchos4mr@gmail.com',
                                            'dpar4fam@hotmail.com',
                                          ].includes(userEmail) && (
                                            <>
                                              <Button
                                                sx={{
                                                  maxWidth: '150px',
                                                  width: '100%',
                                                  color:
                                                    // userType ===
                                                    // 'Indexx Exchange'
                                                    //   ? theme.palette.primary
                                                    //       .main
                                                    //   :
                                                    '#FFA500',
                                                  borderColor: 'none',
                                                  '&:hover': {
                                                    color:
                                                      userType ===
                                                      // 'Indexx Exchange'
                                                      //   ? theme.palette.primary
                                                      //       .main
                                                      //   :
                                                      '#FFA500',
                                                    borderColor: 'none',
                                                    opacity: '.7',
                                                    background: 'none',
                                                  },
                                                }}
                                                onClick={() => {
                                                  setContactPopup(true);
                                                  setType('Switch');
                                                }}
                                              >
                                                Switch Plan
                                              </Button>
                                              <Button
                                                sx={{
                                                  maxWidth: '150px',
                                                  width: '100%',
                                                  color:
                                                    // userType ===
                                                    // 'Indexx Exchange'
                                                    //   ? theme.palette.primary
                                                    //       .main
                                                    //   :
                                                    '#FFA500',
                                                  borderColor: 'none',
                                                  '&:hover': {
                                                    color:
                                                      // userType ===
                                                      // 'Indexx Exchange'
                                                      //   ? theme.palette.primary
                                                      //       .main
                                                      //   :
                                                      '#FFA500',
                                                    borderColor: 'none',
                                                    opacity: '.7',
                                                    background: 'none',
                                                  },
                                                }}
                                                onClick={() => {
                                                  setContactPopup(true);
                                                  setType('Sell');
                                                  setPlanName(
                                                    group.rows[0].notes
                                                  );
                                                  localStorage.setItem(
                                                    'SellPlanCurrencies',
                                                    JSON.stringify(group)
                                                  );
                                                }}
                                              >
                                                Sell Plan
                                              </Button>
                                            </>
                                          )}
                                          {[
                                            'donpanchos4me@gmail.com',
                                            'donpanchos4mr@gmail.com',
                                            'dpar4fam@hotmail.com',
                                          ].includes(userEmail) && (
                                            <Typography
                                              style={{
                                                fontWeight: 800,
                                                color: 'red',
                                              }}
                                            >
                                              This account is already closed or
                                              paid
                                            </Typography>
                                          )}
                                        </div>
                                        <div
                                          className={classes.orderFirstOnTab}
                                        >
                                          <Button
                                            variant="outlined"
                                            sx={{
                                              minWidth: '250px',
                                              width: '100%',
                                              color:
                                                // userType === 'Indexx Exchange'
                                                //   ? theme.palette.primary.main
                                                //   :
                                                '#FFA500',
                                              borderColor:
                                                // userType === 'Indexx Exchange'
                                                //   ? theme.palette.primary.main
                                                //   :
                                                '#FFA500',
                                            }}
                                            onClick={() =>
                                              navigate('/smart-crypto')
                                            }
                                          >
                                            Invest In New Package
                                          </Button>
                                        </div>
                                      </div>
                                    </TableCell>
                                  </TableRow>
                                )}
                                {/* Bottom separator for this package */}
                                <TableRow>
                                  <TableCell
                                    colSpan={isMobile ? 3 : 5}
                                    sx={{
                                      borderBottom:
                                        '1px solid #FFA500 !important',
                                      height: '1px',
                                      padding: 0,
                                    }}
                                  />
                                </TableRow>
                              </React.Fragment>
                            ))}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <TableRow>
                        <TableCell
                          colSpan={isMobile ? 3 : 5}
                          sx={{
                            borderBottom: 'none',
                            fontWeight: 'bold',
                            fontSize: '24px',
                            paddingBottom: '0px',
                            color:
                              // userType === 'Indexx Exchange'
                              //   ? theme.palette.primary.main
                              //   :
                              '#FFA500',
                            textAlign: 'center',
                          }}
                        >
                          <img src={smartCryptoIcon} />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell
                          colSpan={isMobile ? 3 : 5}
                          sx={{ textAlign: 'center' }}
                        >
                          No Smart Crypto packages available
                        </TableCell>
                      </TableRow>
                    </>
                  )}

                  {/* Horizontal divider after Smart Crypto section */}
                  <TableRow>
                    <TableCell
                      colSpan={isMobile ? 3 : 5}
                      sx={{
                        borderBottom: '1px solid #B28314 !important',
                        height: '1px',
                        padding: 0,
                      }}
                    />
                  </TableRow>

                  {/* Smart APY Section */}
                  <>
                    <TableRow>
                      <TableCell
                        colSpan={isMobile ? 3 : 5}
                        sx={{
                          borderBottom: 'none',
                          fontWeight: 'bold',
                          color: theme.palette.primary.main,
                          textAlign: 'center',
                          paddingTop: '20px',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '12px',
                          }}
                        >
                          <img
                            src={smartApyLogo}
                            alt="Smart APY Logo"
                            style={{ height: '30px', width: 'auto' }}
                          />
                          <Typography variant="h6">Smart APY</Typography>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Smart APY Table */}
                    {smartApyData.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={isMobile ? 3 : 5}>
                          <TableContainer
                            component={Paper}
                            sx={{ background: 'none' }}
                          >
                            <Table sx={{ minWidth: isMobile ? 800 : 1100 }}>
                              <TableHead>
                                <TableRow>
                                  <TableCell
                                    align="left"
                                    sx={{ minWidth: 140 }}
                                  >
                                    Coin
                                  </TableCell>
                                  <TableCell
                                    align="left"
                                    sx={{ minWidth: 140 }}
                                  >
                                    Date
                                  </TableCell>
                                  <TableCell
                                    align="left"
                                    sx={{ minWidth: 160 }}
                                  >
                                    Maturity Date
                                  </TableCell>
                                  <TableCell
                                    align="left"
                                    sx={{ minWidth: 160 }}
                                  >
                                    Staked Amount
                                  </TableCell>
                                  {!isMobile && (
                                    <>
                                      <TableCell
                                        align="left"
                                        sx={{ minWidth: 160 }}
                                      >
                                        Lock-up Period
                                      </TableCell>
                                      <TableCell
                                        align="left"
                                        sx={{ minWidth: 120 }}
                                      >
                                        APY Yield
                                      </TableCell>
                                      <TableCell
                                        align="left"
                                        sx={{ minWidth: 160 }}
                                      >
                                        Total Yield
                                      </TableCell>
                                      <TableCell
                                        align="left"
                                        sx={{ minWidth: 160 }}
                                      >
                                        Final Amount
                                      </TableCell>
                                      <TableCell
                                        align="left"
                                        sx={{ minWidth: 160 }}
                                      >
                                        Days to Maturity
                                      </TableCell>
                                    </>
                                  )}
                                </TableRow>
                              </TableHead>

                              <TableBody>
                                {smartApyData.map((row) => {
                                  const daysBeforeEndDate = Math.floor(
                                    (new Date(row.endDate) - new Date()) /
                                      (1000 * 60 * 60 * 24)
                                  );

                                  const daysToMaturityDisplay =
                                    daysBeforeEndDate < 0
                                      ? 'Matured'
                                      : daysBeforeEndDate;
                                  return (
                                    <TableRow key={row.smartApyId}>
                                      <TableCell
                                        align="left"
                                        sx={{
                                          border: 'none !important',
                                          textAlign: 'left !important',
                                        }}
                                      >
                                        <img
                                          src={getImage(row.coin)}
                                          alt={row.coin}
                                          style={{
                                            width: 20,
                                            height: 20,
                                            marginRight: 8,
                                            verticalAlign: 'middle',
                                          }}
                                        />
                                        {row.coin}
                                      </TableCell>

                                      <TableCell
                                        align="left"
                                        sx={{
                                          border: 'none !important',
                                          textAlign: 'left !important',
                                        }}
                                      >
                                        {new Date(
                                          row.startDate
                                        ).toLocaleDateString()}
                                      </TableCell>

                                      <TableCell
                                        align="left"
                                        sx={{
                                          border: 'none !important',
                                          textAlign: 'left !important',
                                        }}
                                      >
                                        {new Date(
                                          row.endDate
                                        ).toLocaleDateString()}
                                      </TableCell>

                                      <TableCell
                                        align="left"
                                        sx={{
                                          border: 'none !important',
                                          textAlign: 'left !important',
                                        }}
                                      >
                                        {row.stakedAmount}
                                      </TableCell>

                                      {!isMobile && (
                                        <>
                                          <TableCell
                                            align="left"
                                            sx={{
                                              border: 'none !important',
                                              textAlign: 'left !important',
                                            }}
                                          >
                                            {row.duration}
                                          </TableCell>
                                          <TableCell
                                            align="left"
                                            sx={{
                                              border: 'none !important',
                                              textAlign: 'left !important',
                                            }}
                                          >
                                            {(row.percentage * 100).toFixed(2)}%
                                          </TableCell>
                                          <TableCell
                                            align="left"
                                            sx={{
                                              border: 'none !important',
                                              textAlign: 'left !important',
                                            }}
                                          >
                                            {row.rewardAmount}
                                          </TableCell>
                                          <TableCell
                                            align="left"
                                            sx={{
                                              border: 'none !important',
                                              textAlign: 'left !important',
                                            }}
                                          >
                                            {row.finalAmount}
                                          </TableCell>
                                          <TableCell
                                            align="left"
                                            sx={{
                                              border: 'none !important',
                                              textAlign: 'left !important',
                                            }}
                                          >
                                            {daysToMaturityDisplay}
                                          </TableCell>
                                        </>
                                      )}
                                    </TableRow>
                                  );
                                })}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </TableCell>
                      </TableRow>
                    )}
                  </>

                  {smartApyData.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={isMobile ? 3 : 5}
                        sx={{ textAlign: 'center' }}
                      >
                        No Smart APY assets available
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
              {/* Horizontal divider after Smart Crypto section */}
              <TableRow>
                <TableCell
                  colSpan={isMobile ? 3 : 5}
                  sx={{
                    borderBottom: '1px solid #B28314 !important',
                    height: '1px',
                    padding: 0,
                  }}
                />
              </TableRow>
              {/* ===================== AI Investment Section ===================== */}
              <TableRow>
                <TableCell colSpan={isMobile ? 3 : 5}>
                  <Box
                    onClick={() => toggleLogo('aiInvestment')}
                    sx={{
                      cursor: 'pointer',
                      // full-width container, center its children
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    {/* inner row kept tight and centered */}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                      }}
                    >
                      {/* square wrapper to cancel any odd transparent padding in the PNG */}
                      <Box
                        sx={{
                          width: { xs: 64, sm: 72, md: 80 },
                          height: { xs: 64, sm: 72, md: 80 },
                          display: 'grid',
                          placeItems: 'center',
                        }}
                      >
                        <Box
                          component="img"
                          src={aiInvestmentLogo}
                          alt="AI Investment"
                          sx={{
                            width: 'auto',
                            transition: 'opacity 0.3s',
                            opacity: expandedLogos.aiInvestment ? 1 : 0.7,
                          }}
                          style={{ height: '30px', width: 'auto' }}
                        />
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 'bold',
                          color: '#BFBFBF !important',
                          fontSize: '20px',
                          fontStyle: 'italic',
                          textAlign: 'center',
                        }}
                      >
                        AI Engine Investment
                      </Typography>
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
              {expandedLogos.aiInvestment && (
                <>
                  {aiNormalized?.length > 0 ? (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 3 : 5}>
                        {/* Smart APY Table — paste this over your existing Smart APY table block */}
                        <TableContainer
                          component={Paper}
                          sx={{ background: 'none' }}
                        >
                          <Table
                            sx={{
                              minWidth: isMobile ? 600 : 1000,
                              tableLayout: 'fixed',
                            }}
                          >
                            <TableHead>
                              <TableRow>
                                {isMobile ? (
                                  <>
                                    <TableCell
                                      sx={{ minWidth: 160 }}
                                      align="left"
                                    >
                                      Date/Time
                                    </TableCell>
                                    <TableCell
                                      sx={{ minWidth: 120 }}
                                      align="left"
                                    >
                                      Type
                                    </TableCell>
                                    <TableCell
                                      sx={{ minWidth: 100 }}
                                      align="right"
                                    >
                                      USD
                                    </TableCell>
                                  </>
                                ) : (
                                  aiHeadCells.map((h) => (
                                    <TableCell
                                      key={h.id}
                                      align={
                                        [
                                          'usdAmount',
                                          'amount',
                                          'priceAtExecution',
                                          'riskLevel',
                                        ].includes(h.id)
                                          ? 'right'
                                          : 'left'
                                      }
                                      sx={{ minWidth: 120 }}
                                    >
                                      {h.label}
                                    </TableCell>
                                  ))
                                )}
                              </TableRow>
                            </TableHead>

                            <TableBody>
                              {aiNormalized.map((row) => (
                                <TableRow key={row.id}>
                                  {isMobile ? (
                                    <>
                                      <TableCell align="left">
                                        {formatDateTime(row.createdAt)}
                                      </TableCell>
                                      <TableCell align="left">
                                        {row.type}
                                      </TableCell>
                                      <TableCell align="right">
                                        {fmtUSD(row.usdAmount)}
                                      </TableCell>
                                    </>
                                  ) : (
                                    <>
                                      <TableCell align="left">
                                        {formatDateTime(row.createdAt)}
                                      </TableCell>
                                      <TableCell align="left">
                                        {row.type}
                                      </TableCell>
                                      <TableCell align="left">
                                        {row.asset}
                                      </TableCell>
                                      <TableCell align="right">
                                        {fmtUSD(row.usdAmount)}
                                      </TableCell>
                                      <TableCell align="right">
                                        {fmtQty(row.amount)}
                                      </TableCell>
                                      <TableCell align="right">
                                        {fmtPrice(row.priceAtExecution)}
                                      </TableCell>
                                      <TableCell align="right">
                                        {Number.isFinite(row.riskLevel)
                                          ? row.riskLevel
                                          : '—'}
                                      </TableCell>
                                      <TableCell align="left">
                                        {row.timeframe}
                                      </TableCell>
                                      <TableCell align="left">
                                        {row.status}
                                      </TableCell>
                                      <TableCell align="left">
                                        {row.side}
                                      </TableCell>
                                      <TableCell align="left">
                                        {row.basket}
                                      </TableCell>
                                    </>
                                  )}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={isMobile ? 3 : 5}
                        sx={{ textAlign: 'center' }}
                      >
                        No AI Investment assets available
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
              {/* Divider after AI Investment */}
              <TableRow>
                <TableCell
                  colSpan={isMobile ? 3 : 5}
                  sx={{
                    borderBottom: '1px solid #BFBFBF !important',
                    height: '1px',
                    padding: 0,
                  }}
                />
              </TableRow>
              {/* Fiat Section - Always shown */}
              <TableRow>
                <TableCell
                  colSpan={isMobile ? 3 : 5}
                  sx={{ textAlign: 'center', paddingTop: '40px' }}
                >
                  <Box
                    onClick={() => toggleLogo('fiat')}
                    sx={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '20px',
                    }}
                  >
                    <img
                      src={fiatImg}
                      alt="Fiat"
                      style={{
                        height: '50px',
                        transition: 'opacity 0.3s',
                        opacity: expandedLogos.fiat ? 1 : 0.7,
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 'bold',
                        color: '#8BB376 !important',
                        fontSize: '20px',
                        fontStyle: 'italic',
                      }}
                    >
                      Fiat / Cash
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
              {expandedLogos.fiat && (
                <>
                  <SectionSubHeader isMobile={isMobile} />
                  {organizedRows
                    .filter((group) => group.category === 'Other Coins')
                    .flatMap((group) => group.rows)
                    .filter((row) => row.coin === 'USD').length > 0 ? (
                    organizedRows
                      .filter((group) => group.category === 'Other Coins')
                      .flatMap((group) => group.rows)
                      .filter((row) => row.coin === 'USD')
                      .map((row, index) => (
                        <TableRow
                          key={`usd-${index}`}
                          sx={{ borderBottom: 'none !important' }}
                        >
                          <TableCell component="th" scope="row" padding="none">
                            <ListItem
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                paddingLeft: 0,
                              }}
                            >
                              <ListItemAvatar
                                sx={{ minWidth: COIN_HEADER_LEFT_OFFSET }}
                              >
                                <Avatar>
                                  <Avatar
                                    alt={`${row.coin}`}
                                    src={getImage(row?.coin)}
                                  />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={row.coin}
                                secondary={`ID: ${row.id}`}
                              />
                              {row.coin?.toUpperCase() === 'USD' ? null : (
                                <>
                                  {isStellarBitcoinYayRow(row) && (
                                    <Chip
                                      size="small"
                                      label="BTCY Nugget"
                                      color="warning"
                                      variant="outlined"
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                  {isYingYangChainRow(row) && (
                                    <Chip
                                      size="small"
                                      label="BTCY Token"
                                      color="warning"
                                      variant="outlined"
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                </>
                              )}
                            </ListItem>
                          </TableCell>
                          <TableCell align="right">
                            {new Intl.NumberFormat('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 6,
                            }).format(row.amount)}{' '}
                            / $
                            {row.amount.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </TableCell>
                          <TableCell align="right">
                            {new Intl.NumberFormat('en-US', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 6,
                            }).format(row.staking_balance)}{' '}
                            / $
                            {(
                              row.staking_balance * row.coin_price
                            ).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </TableCell>
                          {!isMobile && (
                            <>
                              <TableCell align="right">
                                {row.coin_price.toFixed(2)}
                              </TableCell>
                              <TableCell align="right">0.00</TableCell>
                            </>
                          )}
                        </TableRow>
                      ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={isMobile ? 3 : 5}
                        sx={{ textAlign: 'center' }}
                      >
                        No fiat assets available
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
              {/* Horizontal divider after Fiat section */}
              <TableRow>
                <TableCell
                  colSpan={isMobile ? 3 : 5}
                  sx={{
                    borderBottom: '1px solid #7D996B !important',
                    height: '1px',
                    padding: 0,
                  }}
                />
              </TableRow>
            </TableBody>
          ) : (
            <>
              <TableBody>
                {(() => {
                  let hasRenderedXBitcoin = false;
                  let hasRenderedXBlue = false;
                  let lastPackageType = null;
                  const hasSmartCrypto = organizedRows.some(
                    (group) =>
                      group.category.includes('Smart Crypto') &&
                      group.rows.length > 0
                  );

                  const hasCrypto = organizedRows.some(
                    (group) =>
                      isCoinCategory(group.category) && group.rows.length > 0
                  );

                  // Early return if no data for the selected tab
                  const shouldShowNoData =
                    (selectedValue === 'Smart Crypto' && !hasSmartCrypto) ||
                    (selectedValue === 'Cryptos' && !hasCrypto) ||
                    (isBitcoinYaySelection(selectedValue) && !hasBitcoinYay);

                  const renderBitcoinYayHeading = (
                    key = 'bitcoin-yay-heading'
                  ) => (
                    <TableRow key={key}>
                      <TableCell
                        align="center"
                        colSpan={isMobile ? 3 : headCells.length + 2}
                        sx={{
                          paddingTop: '40px',
                          px: '0 !important',
                          textAlign: 'center !important',
                        }}
                      >
                        <Box
                          onClick={() => toggleLogo('bitcoinYay')}
                          sx={{
                            cursor: 'pointer',
                            width: '100%',
                            display: 'flex !important',
                            justifyContent: 'center !important',
                            alignItems: 'center !important',
                          }}
                        >
                          <Box
                            component="img"
                            src={bitcoinYayLogo}
                            alt="Bitcoin Yay"
                            sx={{
                              width: 'auto',
                              transition: 'opacity 0.3s',
                              opacity: expandedLogos.bitcoinYay ? 1 : 0.85,
                              filter: 'brightness(1.2)',
                            }}
                            style={{
                              height: BITCOIN_YAY_LOGO_HEIGHT,
                              width: 'auto',
                            }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );

                  if (shouldShowNoData) {
                    return (
                      <React.Fragment>
                        {selectedValue === 'Smart Crypto' && (
                          <TableRow>
                            <TableCell
                              colSpan={isMobile ? 3 : 5}
                              sx={{ textAlign: 'center' }}
                            >
                              <img
                                src={
                                  selectedValue === 'Smart Crypto'
                                    ? smartCryptoIcon
                                    : ''
                                }
                                style={{ height: '50px' }}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                        {isBitcoinYaySelection(selectedValue) &&
                          !hasBitcoinYay &&
                          renderBitcoinYayHeading('bitcoin-yay-heading-empty')}
                        <TableRow>
                          <TableCell
                            colSpan={isMobile ? 3 : 5}
                            sx={{ textAlign: 'center' }}
                          >
                            No{' '}
                            {selectedValue === 'Smart Crypto'
                              ? 'Smart Crypto packages'
                              : isBitcoinYaySelection(selectedValue)
                              ? 'Bitcoin Yay assets'
                              : 'Crypto Assets'}{' '}
                            available
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  }

                  const rowsToRender = [];

                  if (isBitcoinYaySelection(selectedValue) && hasBitcoinYay) {
                    rowsToRender.push(renderBitcoinYayHeading());
                  }

                  organizedRows.forEach((group, groupIndex) => {
                    const currentPackageType = group.category.includes(
                      'x-Bitcoin'
                    )
                      ? 'x-Bitcoin'
                      : group.category.includes('x-Blue')
                      ? 'x-Blue'
                      : null;

                    const isNewPackageType =
                      currentPackageType &&
                      currentPackageType !== lastPackageType;
                    lastPackageType = currentPackageType;

                    rowsToRender.push(
                      <React.Fragment key={groupIndex}>
                        {/* Add separator before new package type */}
                        {isNewPackageType && (
                          <TableRow>
                            <TableCell
                              colSpan={isMobile ? 3 : 5}
                              sx={{
                                borderBottom: '1px solid #FFA500 !important',
                                height: '1px',
                                padding: 0,
                              }}
                            />
                          </TableRow>
                        )}

                        {/* Render xBitcoin Heading */}
                        {group.category.includes('x-Bitcoin') &&
                          !hasRenderedXBitcoin && (
                            <TableRow>
                              <TableCell
                                colSpan={isMobile ? 3 : 5}
                                sx={{
                                  borderBottom: 'none',
                                  fontWeight: 'bold',
                                  fontSize: '24px',
                                  paddingTop: '20px',
                                  paddingBottom: '0px',
                                  color:
                                    // userType === 'Indexx Exchange'
                                    //   ? theme.palette.primary.main
                                    //   :
                                    '#FFA500',
                                  textAlign: 'center',
                                }}
                              >
                                <img src={smartCryptoIcon} />
                                <img src={xBitcoinIcon} />
                              </TableCell>
                            </TableRow>
                          )}
                        {group.category.includes('x-Bitcoin') &&
                          (hasRenderedXBitcoin = true)}

                        {/* Render xBlue Heading */}
                        {group.category.includes('x-Blue') &&
                          !hasRenderedXBlue && (
                            <TableRow>
                              <TableCell
                                colSpan={isMobile ? 3 : 5}
                                sx={{
                                  borderBottom: 'none',
                                  fontWeight: 'bold',
                                  fontSize: '24px',
                                  paddingTop: '20px',
                                  paddingBottom: '0px',
                                  color:
                                    // userType === 'Indexx Exchange'
                                    //   ? theme.palette.primary.main
                                    //   :
                                    '#FFA500',
                                  textAlign: 'center',
                                }}
                              >
                                <img src={smartCryptoIcon} />
                                <img src={xBlueIcon} />
                              </TableCell>
                            </TableRow>
                          )}
                        {group.category.includes('x-Blue') &&
                          (hasRenderedXBlue = true)}

                        {/* Category Heading */}
                        {!isCoinCategory(group.category) && (
                          <>
                            <TableRow>
                              <TableCell
                                colSpan={isMobile ? 3 : 5}
                                sx={{
                                  borderBottom: 'none',
                                  fontWeight: 'bold',
                                  color:
                                    // userType === 'Indexx Exchange'
                                    //   ? theme.palette.primary.main
                                    //   :
                                    '#FFA500',
                                  textAlign: 'center',
                                }}
                              >
                                <>
                                  {group.category}{' '}
                                  {group.rows.length > 0 &&
                                    getFormattedCategory(group.rows[0].notes)}
                                  {userEmail === 'donpanchos4me@gmail.com' && (
                                    <Typography
                                      className={classes.pnlText}
                                      style={{
                                        fontWeight: 800,
                                        color: 'red',
                                      }}
                                    >
                                      This package was sold by owner on 12th
                                      March
                                    </Typography>
                                  )}
                                </>
                              </TableCell>
                            </TableRow>
                          </>
                        )}
                        {userEmail === 'donpanchos4me@gmail.com'
                          ? ''
                          : !isCoinCategory(group.category) && (
                              <TableRow
                                sx={{ borderBottom: 'none !important' }}
                              >
                                <TableCell
                                  colSpan={isMobile ? 1 : 2}
                                  sx={{
                                    fontSize: '22px',
                                    borderBottom: 'none !important',
                                  }}
                                >
                                  Estimated Value: $
                                  {new Intl.NumberFormat('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }).format(
                                    group.rows.reduce(
                                      (total, row) =>
                                        total + row.amount * row.coin_price,
                                      0
                                    )
                                  )}
                                </TableCell>
                                <TableCell
                                  colSpan={isMobile ? 2 : 2}
                                  sx={{
                                    fontSize: '22px',
                                    borderBottom: 'none !important',
                                  }}
                                >
                                  Invested Amount: $
                                  {new Intl.NumberFormat('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }).format(
                                    group.rows.reduce(
                                      (total, row) =>
                                        total + row.amountInvested,
                                      0
                                    )
                                  )}
                                </TableCell>
                                <TableCell
                                  colSpan={isMobile ? 0 : 2}
                                  sx={{
                                    fontSize: '22px',
                                    borderBottom: 'none !important',
                                    display: isMobile ? 'none' : 'flex',
                                  }}
                                >
                                  Total Value: $
                                  {new Intl.NumberFormat('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }).format(
                                    group.rows.reduce(
                                      (total, row) =>
                                        total +
                                        row.amount * row.coin_price +
                                        row?.staking_balance * row?.coin_price,
                                      0
                                    )
                                  )}
                                </TableCell>
                              </TableRow>
                            )}

                        {/* Rows for each category */}
                        {isCoinCategory(group.category) ? (
                          <>
                            <TableRow>
                              <TableCell
                                colSpan={isMobile ? 3 : 5}
                                sx={{ border: 'none !important' }}
                              >
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: '15px',
                                    alignItems: 'center',
                                    width: '100%',
                                  }}
                                >
                                  <Button
                                    disableRipple
                                    sx={{
                                      maxWidth: '250px',
                                      width: '100%',
                                      color: theme.palette.primary.main,
                                      background: 'none',
                                      borderColor: 'none',
                                      '&:hover': {
                                        color: theme.palette.text.primary,
                                        borderColor: 'none',
                                        opacity: '.7',
                                        background: 'none',
                                      },
                                      '&:active': {
                                        background: 'none',
                                      },
                                      '&:focus': {
                                        background: 'none',
                                      },
                                    }}
                                    onClick={() => {
                                      toggleCategory(group.category);
                                      setIsTotalAmount(!isTotalAmount);
                                    }}
                                  >
                                    {expandedCategories.includes(group.category)
                                      ? 'Hide Details'
                                      : 'See Details'}
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          </>
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={isMobile ? 3 : 5}
                              sx={{ border: 'none !important' }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  justifyContent: 'center',
                                  gap: '15px',
                                  alignItems: 'center',
                                  width: '100%',
                                }}
                              >
                                <Button
                                  disableRipple
                                  sx={{
                                    maxWidth: '250px',
                                    width: '100%',
                                    color:
                                      // userType === 'Indexx Exchange'
                                      //   ? theme.palette.primary.main
                                      //   :
                                      '#FFA500',
                                    background: 'none',
                                    borderColor: 'none',
                                    '&:hover': {
                                      color: theme.palette.text.primary,
                                      borderColor: 'none',
                                      opacity: '.7',
                                      background: 'none',
                                    },
                                    '&:active': {
                                      background: 'none',
                                    },
                                    '&:focus': {
                                      background: 'none',
                                    },
                                  }}
                                  onClick={() => toggleCategory(group.category)}
                                >
                                  {expandedCategories.includes(group.category)
                                    ? 'Hide Details'
                                    : 'See Details'}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}

                        {/* Render rows conditionally */}
                        {expandedCategories.includes(group.category) && (
                          <>
                            <SectionSubHeader isMobile={isMobile} />
                            {group.rows.map((row) => (
                              <TableRow
                                key={row.id}
                                sx={{
                                  borderBottom: 'none !important',
                                }}
                              >
                                <TableCell
                                  component="th"
                                  scope="row"
                                  padding="none"
                                  sx={{ borderBottom: 'none !important' }}
                                >
                                  <ListItem
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      paddingLeft: 0,
                                      '&:hover': {
                                        background: 'transparent !important',
                                      },
                                    }}
                                  >
                                    <ListItemAvatar
                                      sx={{ minWidth: COIN_HEADER_LEFT_OFFSET }}
                                    >
                                      <Avatar>
                                        <Avatar
                                          alt={`${row.coin}`}
                                          src={getImage(row?.coin)}
                                        />
                                      </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                      primary={row.coin}
                                      secondary={`ID: ${row.id}`}
                                    />
                                    {isStellarBitcoinYayRow(row) && (
                                      <Chip
                                        size="small"
                                        label="BTCY Nugget"
                                        color="warning"
                                        variant="outlined"
                                        sx={{ ml: 1 }}
                                      />
                                    )}
                                    {isYingYangChainRow(row) && (
                                      <Chip
                                        size="small"
                                        label="BTCY Token"
                                        color="warning"
                                        variant="outlined"
                                        sx={{ ml: 1 }}
                                      />
                                    )}
                                  </ListItem>
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ borderBottom: 'none !important' }}
                                >
                                  {new Intl.NumberFormat('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 6,
                                  }).format(row.amount)}{' '}
                                  / $
                                  {row.coin === 'USD'
                                    ? row.amount.toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })
                                    : (
                                        row.amount * row.coin_price
                                      ).toLocaleString(undefined, {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{ borderBottom: 'none !important' }}
                                >
                                  {new Intl.NumberFormat('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 6,
                                  }).format(row.staking_balance)}{' '}
                                  / $
                                  {(
                                    row.staking_balance * row.coin_price
                                  ).toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </TableCell>

                                {!isMobile && (
                                  <>
                                    <TableCell
                                      align="right"
                                      sx={{ borderBottom: 'none !important' }}
                                    >
                                      {formatCoinPriceCell(
                                        row.coin,
                                        row.coin_price
                                      )}
                                    </TableCell>
                                    <TableCell
                                      align="right"
                                      sx={{ borderBottom: 'none !important' }}
                                      className={
                                        row.todayPNL
                                          ? row.todayPNL.isPositive
                                            ? classes.greenText
                                            : classes.redText
                                          : ''
                                      }
                                    >
                                      {row.todayPNL
                                        ? `${
                                            row.todayPNL.value
                                          } (${getAdjustedPercentage(
                                            row.coin,
                                            row.todayPNL.percentage
                                          )}%)`
                                        : '0.00'}
                                    </TableCell>
                                  </>
                                )}
                              </TableRow>
                            ))}
                          </>
                        )}

                        {expandedCategories.includes(group.category) &&
                          group.category.includes('Smart Crypto') &&
                          group?.rows?.length > 0 &&
                          calculateTotal(group.rows).totalAmount > 0 && (
                            <TableRow>
                              <TableCell
                                colSpan={isMobile ? 3 : 5}
                                sx={{ border: 'none !important' }}
                              >
                                <div className={classes.flexContainer}>
                                  <div></div>
                                  <div
                                    style={{
                                      display: 'flex',
                                      justifyContent: 'center',
                                      gap: '15px',
                                      alignItems: 'center',
                                      width: '100%',
                                    }}
                                  >
                                    {![
                                      'donpanchos4me@gmail.com',
                                      'donpanchos4mr@gmail.com',
                                      'dpar4fam@hotmail.com',
                                    ].includes(userEmail) && (
                                      <Button
                                        sx={{
                                          maxWidth: '150px',
                                          width: '100%',
                                          color:
                                            // userType === 'Indexx Exchange'
                                            //   ? theme.palette.primary.main
                                            //   :
                                            '#FFA500',
                                          borderColor: 'none',
                                          '&:hover': {
                                            color:
                                              // userType === 'Indexx Exchange'
                                              //   ? theme.palette.primary.main
                                              //   :
                                              '#FFA500',
                                            borderColor: 'none',
                                            opacity: '.7',
                                            background: 'none',
                                          },
                                        }}
                                        onClick={() => {
                                          setContactPopup(true);
                                          setType('Switch');
                                          console.log(
                                            'group.categorygroup.category',
                                            group.category,
                                            group.rows[0].notes
                                          );
                                        }}
                                      >
                                        Switch Plan
                                      </Button>
                                    )}
                                    {![
                                      'donpanchos4me@gmail.com',
                                      'donpanchos4mr@gmail.com',
                                      'dpar4fam@hotmail.com',
                                    ].includes(userEmail) && (
                                      <Button
                                        sx={{
                                          maxWidth: '150px',
                                          width: '100%',
                                          color:
                                            // userType === 'Indexx Exchange'
                                            //   ? theme.palette.primary.main
                                            //   :
                                            '#FFA500',
                                          borderColor: 'none',
                                          '&:hover': {
                                            color:
                                              // userType === 'Indexx Exchange'
                                              //   ? theme.palette.primary.main
                                              //   :
                                              '#FFA500',
                                            borderColor: 'none',
                                            opacity: '.7',
                                            background: 'none',
                                          },
                                        }}
                                        onClick={() => {
                                          setContactPopup(true);
                                          setType('Sell');
                                          setPlanName(group.rows[0].notes);
                                          localStorage.setItem(
                                            'SellPlanCurrencies',
                                            JSON.stringify(group)
                                          );
                                        }}
                                      >
                                        Sell Plan
                                      </Button>
                                    )}

                                    {[
                                      'donpanchos4me@gmail.com',
                                      'donpanchos4mr@gmail.com',
                                      'dpar4fam@hotmail.com',
                                    ].includes(userEmail) && (
                                      <Typography
                                        className={classes.pnlText}
                                        style={{
                                          fontWeight: 800,
                                          color: 'red',
                                        }}
                                      >
                                        This account is already closed or paid
                                      </Typography>
                                    )}
                                  </div>
                                  <div className={classes.orderFirstOnTab}>
                                    <Button
                                      variant="outlined"
                                      sx={{
                                        minWidth: '250px',
                                        width: '100%',
                                        color:
                                          // userType === 'Indexx Exchange'
                                          //   ? theme.palette.primary.main
                                          //   :
                                          '#FFA500',
                                        borderColor:
                                          // userType === 'Indexx Exchange'
                                          //   ? theme.palette.primary.main
                                          //   :

                                          '#FFA500',
                                        '&:hover': {
                                          color:
                                            // userType === 'Indexx Exchange'
                                            //   ? theme.palette.primary.main
                                            //   :
                                            '#FFA500',
                                          borderColor:
                                            // userType === 'Indexx Exchange'
                                            //   ? theme.palette.primary.main
                                            //   :
                                            '#FFA500',
                                          opacity: '.7',
                                          background: 'none',
                                        },
                                      }}
                                      onClick={() => {
                                        navigate('/smart-crypto');
                                      }}
                                    >
                                      Invest In New Package
                                    </Button>
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}

                        {/* Total Row - Only display for non-'Coins' categories */}
                        {isTotalAmount &&
                          isCoinCategory(group.category) &&
                          group.rows.length > 0 && (
                            <>
                              <TableRow>
                                <TableCell
                                  colSpan={isMobile ? 3 : 5}
                                  sx={{
                                    borderBottom: 'none',
                                    fontWeight: 'bold',
                                    textAlign: 'right',
                                  }}
                                >
                                  Total Crypto Amount in USD: $
                                  {new Intl.NumberFormat('en-US', {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }).format(
                                    calculateTotal(group.rows).totalAmount
                                  )}
                                  <br />
                                  {isCoinCategory(group.category) && (
                                    <>
                                      Total Staked Balance in USD: $
                                      {new Intl.NumberFormat('en-US', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }).format(
                                        calculateTotal(group.rows)
                                          .totalStakingBalance
                                      )}
                                    </>
                                  )}
                                </TableCell>
                              </TableRow>
                            </>
                          )}
                        {group.category.includes('Smart Crypto') && (
                          <TableRow>
                            <TableCell
                              colSpan={isMobile ? 3 : 5}
                              sx={{
                                borderBottom: '1px solid #FFA500 !important',
                                fontWeight: 'bold',
                                textAlign: 'right',
                              }}
                            ></TableCell>
                          </TableRow>
                        )}

                        {/* Add separator after each package group */}
                        {currentPackageType && (
                          <TableRow>
                            <TableCell
                              colSpan={isMobile ? 3 : 5}
                              sx={{
                                borderBottom: '1px solid #FFA500 !important',
                                height: '1px',
                                padding: 0,
                              }}
                            />
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  });

                  return rowsToRender;
                })()}
              </TableBody>
            </>
          )}
        </Table>
      </TableContainer>

      {sellConfirmationPopup && (
        <SellConfirmationPopup
          onClose={() => setSellConfirmationPopup(false)}
          category="x-Blue"
          packageName={planName}
          confirmSellProcessed={(userSellPlanReformed, userSellPlan) => {
            setSellConfirmationPopup(false);
            setCongratulationsPopup(true);
            setUserPlanNameReformed(userSellPlanReformed);
            setUserPlanName(userSellPlan);
          }}
        />
      )}

      {CongratulationsPopup && (
        <SellCongratulations
          onClose={() => setCongratulationsPopup(false)}
          category={'x-Blue'}
          userSellPlanReformed={userSellPlanReformed}
          userSellPlan={userSellPlan}
        />
      )}

      {contactPopup && (
        <ContactAccountManagerPopup
          onClose={() => setContactPopup(false)}
          type={type}
        />
      )}
    </Box>
  );
}

EnhancedTable.propTypes = {
  searchQuery: PropTypes.string.isRequired,
  hideAssets: PropTypes.bool.isRequired,
};
